import React, { useState, useEffect, useContext, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AuthContext from "../../context/AuthContext"; // 請確保路徑正確
import "../../css/SpaOrgResponsive.css";
import axios from 'axios';
import { jsPDF } from 'jspdf';
import katlaiLogo from '../../components/logo/katlai-logo-black.png';
import { generateRelaxationReportPDF } from '../../components/generateRelaxationReportPDF.jsx';
import ExportPDFButton from '../../components/ExportPDFButton.jsx';
import PDFReportTemplate from '../../components/PDFReportTemplate.jsx';

// API基本URL
const API_BASE_URL = "https://zz4v8qzou2.execute-api.ap-southeast-1.amazonaws.com";

function OrgDashboard() {
    const [userSettings, setUserSettings] = useState({
        timezone: "Asia/Manila", // 默認 UTC+8 (馬尼拉時區)
        displayFormat: "12h", // 12小時或24小時制
        dateFormat: "MM/DD/YYYY" // 日期格式
    });

    // 添加通知狀態
    const [notification, setNotification] = useState({
        show: false,
        message: '',
        type: 'success' // 'success' 或 'error'
    });

    // 取消治療確認對話框狀態
    const [cancelConfirmation, setCancelConfirmation] = useState({
        show: false,
        treatment: null
    });

    // 取消原因輸入
    const [cancelReason, setCancelReason] = useState('');

    // 刪除確認對話框狀態
    const [deleteConfirmation, setDeleteConfirmation] = useState({
        show: false,
        treatment: null
    });

    // 编辑治疗对话框状态
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingTreatment, setEditingTreatment] = useState(null);

    // 刪除密碼驗證
    const [deletePassword, setDeletePassword] = useState('');
    const [deletePasswordError, setDeletePasswordError] = useState('');

    // 管理員權限檢查
    const [hasAdminPermission, setHasAdminPermission] = useState(false);

    // 隱藏通知
    const hideNotification = () => {
        setNotification({
            ...notification,
            show: false
        });
    };

    // 添加一個函數來處理設置更改
    const handleSettingsChange = (setting, value) => {
        setUserSettings(prev => ({
            ...prev,
            [setting]: value
        }));

        // 保存到 localStorage 以便在頁面刷新後保持設置
        const updatedSettings = { ...userSettings, [setting]: value };
        localStorage.setItem('spaUserSettings', JSON.stringify(updatedSettings));
    };

    // 在初始化時從 localStorage 加載設置
    useEffect(() => {
        const savedSettings = localStorage.getItem('spaUserSettings');
        if (savedSettings) {
            try {
                const parsedSettings = JSON.parse(savedSettings);
                setUserSettings(parsedSettings);
            } catch (e) {
                console.error("Error parsing saved settings:", e);
            }
        }
    }, []);

    // 自動隱藏通知
    useEffect(() => {
        if (notification.show) {
            const timer = setTimeout(() => {
                hideNotification();
            }, 5000); // 5秒後自動隱藏

            return () => clearTimeout(timer);
        }
    }, [notification.show]);

    const { orgId, adminId } = useParams();
    const [orgData, setOrgData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("dashboard");
    const navigate = useNavigate();
    const { user, logout } = useContext(AuthContext); // 使用AuthContext

    // 添加新的狀態用於處理模態框和表單
    const [showTreatmentModal, setShowTreatmentModal] = useState(false);
    const [newTreatment, setNewTreatment] = useState({
        therapistName: "",
        guestFirstName: "",
        guestLastName: "",
        guestEmail: "",
        countryCode: "",
        phoneNumber: "",
        spaRoomNumber: "",
        date: "",
        startTimeHour: "",
        startTimeMinute: "",
        startTimeAmPm: "AM",
        isPregnant: false,
        hasRespiratoryConcerns: false,
        treatmentType: "",
        treatmentDuration: "",
    });

    // 新增狀態用於存儲API加載的數據
    const [dashboardData, setDashboardData] = useState({
        activeSessions: 0,
        totalTreatments: 0,
        todaysTreatments: 0,
        upcomingBookings: 0,
        recentActivity: []
    });
    const [treatments, setTreatments] = useState([]);
    const [statisticsLoading, setStatisticsLoading] = useState(true);
    const [treatmentsLoading, setTreatmentsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshing, setRefreshing] = useState(false); // 添加刷新狀態


    // 用於訪問碼頁面
    const [selectedTreatment, setSelectedTreatment] = useState(null);
    const [accessCode, setAccessCode] = useState(null);
    const [accessCodeLoading, setAccessCodeLoading] = useState(false);

    // 用於激活客戶頁面
    const [activatedCustomers, setActivatedCustomers] = useState([]);
    const [customersLoading, setCustomersLoading] = useState(false);
    const [customerFilters, setCustomerFilters] = useState({
        status: '',
        therapistName: '',
        treatmentType: '',
        startDate: '',
        endDate: ''
    });
    const [customerSearch, setCustomerSearch] = useState('');

    // Add this to your existing state declarations
    const [isPdfExporting, setIsPdfExporting] = useState(false);

    // 添加移动端菜单状态
    const [menuOpen, setMenuOpen] = useState(false);
    const sidebarRef = useRef(null);

    // 加載組織數據
    useEffect(() => {
        // 如果有使用Auth系統，優先使用Auth中的數據
        if (user && user.role === "orgAdmin") {
            setOrgData({
                orgId: user.organisationID,
                adminId: user.id,
                orgName: user.orgName,
                adminName: user.name,
                role: user.role // 設置角色
            });
            setHasAdminPermission(user.role === 'admin' || user.role === 'superAdmin');
            setLoading(false);
            return;
        }

        // 否則從路由參數獲取數據
        const fetchOrgData = () => {
            setLoading(true);

            // Organisation lookup table
            const orgDatabase = [
                { orgId: "H48", adminId: "83", orgName: "Swissotel Clark", adminName: "Admin1", role: "admin" },
                { orgId: "H72", adminId: "91", orgName: "Parkinn Clark", adminName: "Admin1", role: "admin" },
                { orgId: "H77", adminId: "64", orgName: "Reef Cebu", adminName: "Admin1", role: "admin" },
                { orgId: "H28", adminId: "68", orgName: "Midas Manila", adminName: "Admin1", role: "admin" },
                { orgId: "H16", adminId: "31", orgName: "Shangri-la Cebu", adminName: "Admin1", role: "admin" },
                { orgId: "H11", adminId: "25", orgName: "Marriott Spa Clark", adminName: "Admin1", role: "admin" },
                { orgId: "S38", adminId: "89", orgName: "Okada Spa", adminName: "Admin1", role: "admin" },
                { orgId: "S69", adminId: "30", orgName: "Swissotel Spa", adminName: "Admin1", role: "admin" },
                { orgId: "S93", adminId: "15", orgName: "Marriott Spa Clark", adminName: "Admin1", role: "admin" }
            ];

            // Find matching organisation
            const matchedOrg = orgDatabase.find(
                org => org.orgId === orgId && org.adminId === adminId
            );

            if (matchedOrg) {
                setTimeout(() => {
                    setOrgData(matchedOrg);
                    setHasAdminPermission(matchedOrg.role === 'admin' || matchedOrg.role === 'superAdmin');
                    setLoading(false);
                }, 800); // Simulate loading delay
            } else {
                // Handle invalid credentials
                setTimeout(() => {
                    navigate("/org/login");
                    setLoading(false);
                }, 800);
            }
        };

        fetchOrgData();
    }, [orgId, adminId, navigate, user]);

    // 加載統計數據和療程列表
    useEffect(() => {
        if (orgData) {
            fetchTreatmentStatistics();
            fetchRecentTreatments();
        }
    }, [orgData]);

    // 檢查並更新治療狀態（如超時則標記為 "ended"）
    useEffect(() => {
        const interval = setInterval(() => {
            if (treatments.length > 0) {
                const now = new Date();
                const updatedTreatments = treatments.map(treatment => {
                    // 如果狀態為 scheduled，且已超過結束時間，則標記為 "ended"
                    if (treatment.status === 'scheduled') {
                        const startTime = new Date(treatment.startTime);
                        const endTime = new Date(treatment.endTime ||
                            new Date(startTime.getTime() + parseInt(treatment.duration) * 60000));

                        if (now > endTime) {
                            return { ...treatment, status: 'ended' };
                        }
                    }
                    return treatment;
                });

                // 只有當有治療狀態更新時才更新狀態
                const hasChanges = updatedTreatments.some((t, i) => t.status !== treatments[i].status);
                if (hasChanges) {
                    setTreatments(updatedTreatments);
                }
            }
        }, 60000); // 每分鐘檢查一次

        return () => clearInterval(interval);
    }, [treatments]);

    // 獲取療程統計數據
    const fetchTreatmentStatistics = async () => {
        setStatisticsLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/getTreatmentsStatistics-v1?orgId=${orgData.orgId}`);

            if (!response.ok) {
                throw new Error(`API error: ${response.status}`);
            }

            const data = await response.json();

            // 使用固定的 UTC+8 時區偏移 (香港時間)
            const timezoneOffset = 8 * 60 * 60 * 1000; // 8小時

            // 獲取調整後的當前時間
            const now = new Date();
            const localNow = new Date(now.getTime() + timezoneOffset + now.getTimezoneOffset() * 60 * 1000);

            // 手動計算當前激活的治療數量
            let activeSessionsCount = 0;
            if (data.treatments) {
                activeSessionsCount = data.treatments.filter(treatment => {
                    // 只考慮狀態為 'in-progress' 的治療，或者是應該正在進行的治療
                    if (treatment.status === 'in-progress') return true;

                    if (treatment.status === 'scheduled') {
                        // 解析治療開始和結束時間
                        const startTime = new Date(treatment.startTime);
                        const duration = parseInt(treatment.duration || 60);
                        const endTime = treatment.endTime ?
                            new Date(treatment.endTime) :
                            new Date(startTime.getTime() + duration * 60000);

                        // 檢查治療是否正在進行中 (當前時間在開始和結束時間之間)
                        return localNow >= startTime && localNow <= endTime;
                    }

                    return false;
                }).length;
            }

            // 手動計算即將到來的預約
            let upcomingCount = 0;
            if (data.treatments) {
                upcomingCount = data.treatments.filter(treatment => {
                    // 只考慮已排程的治療
                    if (treatment.status !== 'scheduled') return false;

                    // 解析治療開始時間並應用時區調整
                    const startTime = new Date(treatment.startTime);

                    // 檢查治療是否在未來24小時內
                    return startTime > localNow && startTime <= new Date(localNow.getTime() + 24 * 60 * 60 * 1000);
                }).length;
            }

            // 更新儀表板數據
            setDashboardData({
                activeSessions: activeSessionsCount, // 使用手動計算的激活治療數量
                totalTreatments: data.totalTreatments || 0,
                todaysTreatments: data.todaysTreatments || 0,
                upcomingBookings: upcomingCount,
                recentActivity: [] // 將在 fetchRecentTreatments 中填充
            });

            setStatisticsLoading(false);
        } catch (err) {
            console.error("Error fetching statistics:", err);
            setError(err.message);
            setStatisticsLoading(false);
        }
    };

    // 獲取最近的療程
    const fetchRecentTreatments = async () => {
        setTreatmentsLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/listAllTreatment-v1?orgId=${orgData.orgId}`);

            if (!response.ok) {
                throw new Error(`API error: ${response.status}`);
            }

            const data = await response.json();

            // 使用固定的時區偏移 (香港時間 UTC+8)
            const timezoneOffset = 8 * 60 * 60 * 1000; // 8小時

            // 獲取調整後的當前時間
            const now = new Date();
            const localNow = new Date(now.getTime() + timezoneOffset + now.getTimezoneOffset() * 60 * 1000);

            const processedTreatments = data.treatments.map(treatment => {
                // 解析並調整治療時間
                const startTime = new Date(treatment.startTime);
                const duration = parseInt(treatment.duration || 60);
                const endTime = treatment.endTime ?
                    new Date(treatment.endTime) :
                    new Date(startTime.getTime() + duration * 60000);

                // 確定治療的實際狀態
                let updatedStatus = treatment.status;

                // 如果狀態為 scheduled
                if (treatment.status === 'scheduled') {
                    if (localNow > endTime) {
                        // 已經結束
                        updatedStatus = 'ended';
                    } else if (localNow >= startTime && localNow <= endTime) {
                        // 正在進行中
                        updatedStatus = 'in-progress';
                    }
                    // 否則保持為 scheduled
                }

                return { ...treatment, status: updatedStatus };
            });

            // 排序療程按日期從新到舊
            const sortedTreatments = processedTreatments.sort((a, b) =>
                new Date(b.startTime) - new Date(a.startTime)
            );

            setTreatments(sortedTreatments);

            // 計算當前激活的治療數量
            const activeSessionsCount = sortedTreatments.filter(treatment =>
                treatment.status === 'in-progress'
            ).length;

            // 計算未來24小時內的預約
            const upcomingBookings = sortedTreatments.filter(treatment => {
                if (treatment.status !== 'scheduled') return false;

                const startTime = new Date(treatment.startTime);
                return startTime > localNow && startTime <= new Date(localNow.getTime() + 24 * 60 * 60 * 1000);
            }).length;

            // 更新儀表板數據
            setDashboardData(prev => ({
                ...prev,
                activeSessions: activeSessionsCount,
                upcomingBookings: upcomingBookings,
                // 同時更新最近活動列表
                recentActivity: sortedTreatments.slice(0, 5).map(treatment => {
                    let action = "Treatment scheduled";
                    if (treatment.status === 'in-progress') {
                        action = "Treatment in progress";
                    } else if (treatment.status === 'completed') {
                        action = "Treatment completed";
                    } else if (treatment.status === 'cancelled') {
                        action = "Treatment cancelled";
                    } else if (treatment.status === 'ended') {
                        action = "Treatment ended";
                    }

                    return {
                        time: formatTime(treatment.startTime),
                        action: action,
                        room: treatment.roomNumber,
                        therapyType: treatment.treatmentType
                    };
                })
            }));

            setTreatmentsLoading(false);
        } catch (err) {
            console.error("Error fetching treatments:", err);
            setError(err.message);
            setTreatmentsLoading(false);
        }
    };

    // 添加刷新數據功能
    const handleRefreshData = async () => {
        setRefreshing(true);
        try {
            await Promise.all([
                fetchTreatmentStatistics(),
                fetchRecentTreatments()
            ]);
            setTimeout(() => {
                setRefreshing(false);
            }, 800); // 讓刷新圖標顯示至少800毫秒
        } catch (err) {
            console.error("Error refreshing data:", err);
            setRefreshing(false);
        }
    };

    const handleLogout = () => {
        if (logout) logout(); // 使用AuthContext的logout函數
        navigate("/org/login");
    };

    // 格式化時間，應用 UTC+8 時區
    const formatTime = (dateString) => {
        if (!dateString) return '';

        const date = new Date(dateString);

        // 使用固定的時區偏移 (香港時間 UTC+8)
        const timezoneOffset = 8 * 60 * 60 * 1000; // 8小時

        // 調整日期以反映 UTC+8 時區
        const adjustedDate = new Date(date.getTime() + timezoneOffset + date.getTimezoneOffset() * 60 * 1000);

        // 獲取小時和分鐘
        const hours = adjustedDate.getHours();
        const minutes = adjustedDate.getMinutes();

        // 根據用戶設置選擇 12 小時制或 24 小時制
        const use24HourFormat = true; // 或從其他地方獲取設置
        if (use24HourFormat) {
            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        } else {
            const displayHours = hours % 12 || 12; // 12小時制轉換
            const ampm = hours >= 12 ? 'PM' : 'AM';
            return `${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
        }
    };

    // 格式化日期，應用 UTC+8 時區
    const formatDate = (dateString) => {
        if (!dateString) return '';

        const date = new Date(dateString);

        // 使用固定的時區偏移 (香港時間 UTC+8)
        const timezoneOffset = 8 * 60 * 60 * 1000; // 8小時

        // 調整日期以反映 UTC+8 時區
        const adjustedDate = new Date(date.getTime() + timezoneOffset + date.getTimezoneOffset() * 60 * 1000);

        // 獲取日、月、年
        const day = adjustedDate.getDate();
        const month = adjustedDate.getMonth() + 1; // 月份是從0開始的
        const year = adjustedDate.getFullYear();

        // 返回格式化的日期字符串 DD/MM/YYYY
        return `${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${year}`;
    };

    // 從ISO日期字符串中提取日期部分 (YYYY-MM-DD)
    const extractDateFromISO = (isoString) => {
        if (!isoString) return '';
        return isoString.split('T')[0];
    };

    // 從ISO日期字符串中提取時間部分，並轉換為12小時制的時、分和AM/PM
    const extractTimeFromISO = (isoString) => {
        if (!isoString) return { hour: '', minute: '', ampm: 'AM' };

        const date = new Date(isoString);
        const timezoneOffset = 8 * 60 * 60 * 1000; // UTC+8 = 8小時
        const adjustedDate = new Date(date.getTime() + timezoneOffset);

        let hour = adjustedDate.getUTCHours();
        const minute = adjustedDate.getUTCMinutes().toString().padStart(2, '0');
        const ampm = hour >= 12 ? 'PM' : 'AM';

        // 轉換成12小時制
        hour = hour % 12 || 12;

        return {
            hour: hour.toString(),
            minute,
            ampm
        };
    };

    // 獲取治療狀態標籤樣式
    const getStatusStyle = (status) => {
        switch (status) {
            case 'scheduled':
                return styles.statusScheduled;
            case 'in-progress':
                return styles.statusInProgress;
            case 'completed':
                return styles.statusCompleted;
            case 'cancelled':
                return styles.statusCancelled;
            case 'ended':
                return styles.statusEnded;
            default:
                return styles.statusScheduled;
        }
    };

    // 獲取狀態顯示顏色
    const getStatusColor = (status) => {
        switch (status) {
            case 'scheduled':
                return '#4caf50'; // 綠色
            case 'in-progress':
                return '#2196f3'; // 藍色
            case 'completed':
                return '#8d6e63'; // 棕色
            case 'cancelled':
                return '#f44336'; // 紅色
            case 'ended':
                return '#9e9e9e'; // 灰色
            default:
                return '#9e9e9e'; // 灰色
        }
    };

    // 格式化狀態顯示
    const formatStatus = (status) => {
        switch (status) {
            case 'scheduled':
                return 'Scheduled';
            case 'in-progress':
                return 'In Progress';
            case 'completed':
                return 'Completed';
            case 'cancelled':
                return 'Cancelled';
            case 'ended':
                return 'Ended';
            default:
                return status;
        }
    };

    // 新增的函數 - 處理表單輸入變化
    const handleTreatmentInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setNewTreatment(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // 处理编辑表单输入变化
    const handleEditTreatmentInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditingTreatment(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // 新增的函數 - 處理新療程提交
    const handleCreateTreatment = async (e) => {
        e.preventDefault();

        try {
            // 解析用戶輸入的時間
            const { date, startTimeHour, startTimeMinute, startTimeAmPm, treatmentDuration } = newTreatment;
            let hour24 = parseInt(startTimeHour);

            // 轉換為24小時制
            if (startTimeAmPm === "PM" && hour24 < 12) hour24 += 12;
            if (startTimeAmPm === "AM" && hour24 === 12) hour24 = 0;

            // 創建一個 ISO 字符串，明確包含時區偏移 (+08:00 for UTC+8)
            // 注意：這已經是本地時間 (UTC+8)，不需要額外的時區調整
            const localISOString = `${date}T${hour24.toString().padStart(2, '0')}:${startTimeMinute.toString().padStart(2, '0')}:00+08:00`;

            // 創建日期對象並轉換為 UTC ISO 字符串
            // 這會自動將本地時間 (UTC+8) 轉換為 UTC
            const localDate = new Date(localISOString);
            const startTimeISO = localDate.toISOString(); // 此時已轉換為 UTC

            // 計算結束時間 (UTC)
            const endDate = new Date(localDate.getTime() + parseInt(treatmentDuration) * 60 * 1000);
            const endTimeISO = endDate.toISOString();

            // 使用當地日期格式 (YYYYMMDD)，不包括時區轉換
            const dateYYYYMMDD = date.replace(/-/g, '');

            const price = calculatePrice(newTreatment.treatmentType, newTreatment.treatmentDuration);

            const treatmentData = {
                orgId: orgData.orgId,
                adminId: orgData.adminId,
                therapistId: getTherapistId(newTreatment.therapistName),
                therapistName: newTreatment.therapistName,
                guestFirstName: newTreatment.guestFirstName,
                guestLastName: newTreatment.guestLastName,
                guestEmail: newTreatment.guestEmail || '',
                guestPhone: `${newTreatment.countryCode}${newTreatment.phoneNumber}` || '',
                roomNumber: newTreatment.spaRoomNumber,
                startTime: startTimeISO,
                endTime: endTimeISO,
                date: date,
                dateYYYYMMDD: dateYYYYMMDD,
                treatmentType: newTreatment.treatmentType,
                duration: parseInt(treatmentDuration),
                status: "scheduled",
                isPregnant: newTreatment.isPregnant,
                hasRespiratoryConcerns: newTreatment.hasRespiratoryConcerns,
                price: price,
                notes: ""
            };

            // 發送請求創建新療程
            const response = await fetch(`${API_BASE_URL}/createTreatment-v1`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(treatmentData),
            });

            if (!response.ok) {
                throw new Error(`API error: ${response.status}`);
            }

            const result = await response.json();

            // 重置表單並關閉模態框
            setNewTreatment({
                therapistName: "",
                guestFirstName: "",
                guestLastName: "",
                guestEmail: "",
                countryCode: "",
                phoneNumber: "",
                spaRoomNumber: "",
                date: "",
                startTimeHour: "",
                startTimeMinute: "",
                startTimeAmPm: "AM",
                isPregnant: false,
                hasRespiratoryConcerns: false,
                treatmentType: "",
                treatmentDuration: "",
            });
            setShowTreatmentModal(false);

            // 刷新數據
            fetchTreatmentStatistics();
            fetchRecentTreatments();

            // 顯示成功通知
            setNotification({
                show: true,
                message: "Treatment created successfully!",
                type: "success"
            });

        } catch (err) {
            console.error("Error creating treatment:", err);
            setNotification({
                show: true,
                message: `Error creating treatment: ${err.message}`,
                type: "error"
            });
        }
    };

    // 处理编辑治疗
    const handleEditTreatment = (treatment) => {
        // 从ISO时间中提取日期和时间信息
        const date = extractDateFromISO(treatment.startTime);
        const { hour, minute, ampm } = extractTimeFromISO(treatment.startTime);

        // 设置编辑表单数据
        setEditingTreatment({
            ...treatment,
            date: date,
            startTimeHour: hour,
            startTimeMinute: minute,
            startTimeAmPm: ampm,
            countryCode: treatment.guestPhone ? treatment.guestPhone.substring(0, 3) : "",
            phoneNumber: treatment.guestPhone ? treatment.guestPhone.substring(3) : "",
            treatmentDuration: treatment.duration.toString()
        });

        // 打开编辑模态框
        setEditModalOpen(true);
    };

    // 提交编辑表单
    const handleSubmitEdit = async (e) => {
        e.preventDefault();

        try {
            // 解析用户输入的时间
            const { date, startTimeHour, startTimeMinute, startTimeAmPm, treatmentDuration } = editingTreatment;
            let hour24 = parseInt(startTimeHour);

            // 转换为24小时制
            if (startTimeAmPm === "PM" && hour24 < 12) hour24 += 12;
            if (startTimeAmPm === "AM" && hour24 === 12) hour24 = 0;

            // 创建一个 ISO 字符串，明确包含时区偏移 (+08:00 for UTC+8)
            const localISOString = `${date}T${hour24.toString().padStart(2, '0')}:${startTimeMinute.toString().padStart(2, '0')}:00+08:00`;

            // 创建日期对象并转换为 UTC ISO 字符串
            const localDate = new Date(localISOString);
            const startTimeISO = localDate.toISOString();

            // 计算结束时间 (UTC)
            const endDate = new Date(localDate.getTime() + parseInt(treatmentDuration) * 60 * 1000);
            const endTimeISO = endDate.toISOString();

            // 使用当地日期格式 (YYYYMMDD)
            const dateYYYYMMDD = date.replace(/-/g, '');

            // 计算价格
            const price = calculatePrice(editingTreatment.treatmentType, editingTreatment.treatmentDuration);

            const orgId = orgData.orgId;
            const treatmentId = editingTreatment.treatmentId;

            // 准备更新数据
            const treatmentData = {
                therapistId: getTherapistId(editingTreatment.therapistName),
                therapistName: editingTreatment.therapistName,
                guestFirstName: editingTreatment.guestFirstName,
                guestLastName: editingTreatment.guestLastName,
                guestEmail: editingTreatment.guestEmail || '',
                guestPhone: `${editingTreatment.countryCode}${editingTreatment.phoneNumber}` || '',
                roomNumber: editingTreatment.roomNumber,
                startTime: startTimeISO,
                endTime: endTimeISO,
                date: date,
                dateYYYYMMDD: dateYYYYMMDD,
                treatmentType: editingTreatment.treatmentType,
                duration: parseInt(treatmentDuration),
                status: editingTreatment.status,
                isPregnant: editingTreatment.isPregnant,
                hasRespiratoryConcerns: editingTreatment.hasRespiratoryConcerns,
                price: price,
                updatedAt: new Date().toISOString(),
                updatedBy: orgData.adminName || 'Admin'
            };

            console.log(`Updating treatment: ${orgId}/${treatmentId}`, treatmentData);

            // 发送更新请求 - 使用正确的URL格式
            const response = await fetch(`${API_BASE_URL}/updateTreatment-v1`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    orgId: orgId,
                    treatmentId: treatmentId,
                    ...treatmentData
                }),
            });

            if (!response.ok) {
                throw new Error(`API error: ${response.status}`);
            }

            const result = await response.json();

            // 关闭模态框
            setEditModalOpen(false);
            setEditingTreatment(null);

            // 刷新数据
            fetchTreatmentStatistics();
            fetchRecentTreatments();

            // 显示成功通知
            setNotification({
                show: true,
                message: "Treatment updated successfully!",
                type: "success"
            });

        } catch (err) {
            console.error("Error updating treatment:", err);
            setNotification({
                show: true,
                message: `Error updating treatment: ${err.message}`,
                type: "error"
            });
        }
    };

    // 将Quick Cancel改为Remove Treatment
    const handleToggleTreatmentStatus = async (treatment) => {
        try {
            // 设置载入状态
            setTreatmentsLoading(true);

            // 准备请求数据
            const treatmentId = treatment.treatmentId;
            const orgId = orgData.orgId;

            // 如果是取消状态，恢复为scheduled；否则执行删除操作
            if (treatment.status === 'cancelled') {
                // 恢复功能 - 将取消状态改为scheduled
                console.log(`Restoring treatment: ${orgId}/${treatmentId} to scheduled status`);

                const response = await fetch(`${API_BASE_URL}/updateTreatmentStatus-v1`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        orgId: orgId,
                        treatmentId: treatmentId,
                        status: 'scheduled',
                        restoredBy: orgData.adminName || 'Admin',
                        restoredAt: new Date().toISOString()
                    }),
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || `API error: ${response.status}`);
                }

                // 显示成功通知
                setNotification({
                    show: true,
                    message: 'Treatment restored to scheduled status',
                    type: 'success'
                });

                // 更新本地治疗数据状态
                setTreatments(treatments.map(t =>
                    t.treatmentId === treatmentId ? { ...t, status: 'scheduled' } : t
                ));
            } else {
                // 删除功能 - 永久删除治疗记录
                console.log(`Permanently removing treatment: ${orgId}/${treatmentId}`);

                const response = await fetch(`${API_BASE_URL}/deleteTreatment-v1`, {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        orgId: orgId,
                        id: treatmentId,  // 注意这里用id而不是treatmentId (根据API要求)
                        deletedBy: orgData.adminName || 'Admin',
                        deletedAt: new Date().toISOString()
                    }),
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || `API error: ${response.status}`);
                }

                // 显示成功通知
                setNotification({
                    show: true,
                    message: 'Treatment permanently removed',
                    type: 'success'
                });

                // 从本地治疗列表中移除
                setTreatments(treatments.filter(t => t.treatmentId !== treatmentId));
            }

            // 重新加载治疗列表和统计数据
            fetchRecentTreatments();
            fetchTreatmentStatistics();

        } catch (err) {
            console.error("Error updating treatment status:", err);

            // 显示错误通知
            setNotification({
                show: true,
                message: `Error: ${err.message}`,
                type: 'error'
            });
        } finally {
            setTreatmentsLoading(false);
        }
    };

    // 假設的輔助函數
    const getTherapistId = (name) => {
        const therapists = {
            "Rea Hosana": "TH001",
            "Michelle Abalos": "TH002",
            "Grace Marasigan": "TH003",
            "Edlyn Molina": "TH004",
            "Mary Ann Quiazon": "TH005",
            "Bernalie Torres": "TH006",
            "Daisy Pangilinan": "TH007",
            "Loisa Gelito": "TH008",
            "Mary Jane Pamintuan": "TH009"
        };
        return therapists[name] || "TH000";
    };

    const formatDateTimeToISO = (date, time, ampm) => {
        const [hours, minutes] = time.split(':');
        let hour = parseInt(hours);

        // 處理AM/PM
        if (ampm === "PM" && hour < 12) hour += 12;
        if (ampm === "AM" && hour === 12) hour = 0;

        const formattedHour = hour.toString().padStart(2, '0');
        return `${date}T${formattedHour}:${minutes}:00.000Z`;
    };

    const calculateEndTime = (date, startTime, ampm, duration) => {
        const [hours, minutes] = startTime.split(':');
        let hour = parseInt(hours);

        // 處理AM/PM
        if (ampm === "PM" && hour < 12) hour += 12;
        if (ampm === "AM" && hour === 12) hour = 0;

        const startDate = new Date(`${date}T${hour.toString().padStart(2, '0')}:${minutes}:00`);
        const endDate = new Date(startDate.getTime() + parseInt(duration) * 60000);

        return endDate.toISOString();
    };

    const calculatePrice = (treatmentType, duration) => {
        // 根據療程類型和時間計算價格
        const basePrice = {
            "Wellness for Two": 4500,
            "Pure Radiance": 3500,
            "Vitality Booster": 3000,
            "Mekeni Signature Massage": 2800,
            "Purovel Mountain Stone Massage": 3200,
            "Refreshing Relaxation Massage": 2500,
            "Wellbeing Muscle Recovery Massage": 2700,
            "East Asian Massage": 2600,
            "Sole & Leg Experience": 1800,
            "Bespoke Massage": 3500,
            "Ultra Comprehensive Anti-Aging Facial": 3800,
            "Comforting & Soothing Facial": 3200,
            "New Skin for Men": 3000,
            "Perfect Contour Scrub": 2200,
            "HImalayan Salt & Coconut Oil Scrub": 2400
        };

        const durMultiplier = {
            "45": 0.8,
            "50": 0.85,
            "60": 1,
            "75": 1.15,
            "90": 1.3,
            "120": 1.6,
            "150": 1.9,
            "180": 2.2
        };

        return basePrice[treatmentType] * durMultiplier[duration] || 0;
    };

    // 处理取消提示
    const handleCancelPrompt = (treatment) => {
        setCancelConfirmation({
            show: true,
            treatment: treatment
        });
        setCancelReason(''); // 重置取消原因
    };

    // 关闭取消确认框
    const handleCloseCancelDialog = () => {
        setCancelConfirmation({
            show: false,
            treatment: null
        });
        setCancelReason('');
    };

    // 处理取消原因变更
    const handleCancelReasonChange = (e) => {
        setCancelReason(e.target.value);
    };

    // 执行取消治疗
    const handleCancelTreatment = async () => {
        if (!cancelConfirmation.treatment) return;

        try {
            // 设置载入状态
            setTreatmentsLoading(true);

            // 准备取消请求数据
            const treatmentId = cancelConfirmation.treatment.treatmentId;
            const orgId = orgData.orgId;

            console.log(`Cancelling treatment: ${orgId}/${treatmentId}`);

            // 执行更新状态请求
            const response = await fetch(`${API_BASE_URL}/updateTreatmentStatus-v1`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    orgId: orgId,
                    treatmentId: treatmentId,
                    status: 'cancelled',
                    cancelReason: cancelReason || 'No reason provided',
                    cancelledBy: orgData.adminName || 'Admin',
                    cancelledAt: new Date().toISOString()
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `API error: ${response.status}`);
            }

            // 关闭确认对话框
            handleCloseCancelDialog();

            // 更新本地治疗数据状态
            setTreatments(treatments.map(t =>
                t.treatmentId === treatmentId
                    ? {
                        ...t,
                        status: 'cancelled',
                        cancelReason: cancelReason || 'No reason provided',
                        cancelledBy: orgData.adminName || 'Admin',
                        cancelledAt: new Date().toISOString()
                    }
                    : t
            ));

            // 显示成功通知
            setNotification({
                show: true,
                message: 'Treatment cancelled successfully',
                type: 'success'
            });

            // 重新加载治疗列表和统计数据
            fetchRecentTreatments();
            fetchTreatmentStatistics();

        } catch (err) {
            console.error("Error cancelling treatment:", err);

            // 显示错误通知
            setNotification({
                show: true,
                message: `Error cancelling treatment: ${err.message}`,
                type: 'error'
            });
        } finally {
            setTreatmentsLoading(false);
        }
    };

    // 处理删除提示
    const handleDeletePrompt = (treatment) => {
        setDeleteConfirmation({
            show: true,
            treatment: treatment
        });
        setDeletePassword('');
        setDeletePasswordError('');
    };

    // 关闭删除确认框
    const handleCloseDeleteDialog = () => {
        setDeleteConfirmation({
            show: false,
            treatment: null
        });
        setDeletePassword('');
        setDeletePasswordError('');
    };

    // 处理密码输入变更
    const handleDeletePasswordChange = (e) => {
        setDeletePassword(e.target.value);
        setDeletePasswordError('');
    };

    // 执行永久删除治疗
    const handleDeleteTreatment = async () => {
        if (!deleteConfirmation.treatment) return;

        // 验证管理员密码
        if (deletePassword !== 'confirm') { // 实际应用中应使用更安全的验证方式
            setDeletePasswordError('Incorrect password');
            return;
        }

        try {
            // 设置载入状态
            setTreatmentsLoading(true);

            // 准备删除请求数据
            const treatmentId = deleteConfirmation.treatment.treatmentId;
            const orgId = orgData.orgId;

            console.log(`Deleting treatment permanently: ${orgId}/${treatmentId}`);

            // 执行删除请求
            const response = await fetch(`${API_BASE_URL}/deleteTreatment-v1`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    orgId: orgId,
                    id: treatmentId,
                    deletedBy: orgData.adminName || 'Admin',
                    deletedAt: new Date().toISOString()
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `API error: ${response.status}`);
            }

            // 关闭确认对话框
            handleCloseDeleteDialog();

            // 更新治疗列表 (移除已删除的治疗)
            setTreatments(treatments.filter(t => t.treatmentId !== treatmentId));

            // 显示成功通知
            setNotification({
                show: true,
                message: 'Treatment permanently deleted',
                type: 'success'
            });

            // 重新加载治疗列表和统计数据
            fetchRecentTreatments();
            fetchTreatmentStatistics();

        } catch (err) {
            console.error("Error deleting treatment:", err);

            // 显示错误通知
            setNotification({
                show: true,
                message: `Error deleting treatment: ${err.message}`,
                type: 'error'
            });
        } finally {
            setTreatmentsLoading(false);
        }
    };

    // 生成訪問碼 - 修正版
    const handleGenerateAccessCode = async (treatment) => {
        setSelectedTreatment(treatment);
        setAccessCodeLoading(true);

        try {
            // 檢查治療記錄是否有 clientId
            if (!treatment.clientId) {
                throw new Error("無法生成訪問碼：此治療記錄沒有關聯的客戶 ID");
            }

            const response = await fetch("https://nkzee8u8cc.execute-api.ap-southeast-1.amazonaws.com/generateAccessCode", {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    customerId: treatment.clientId
                }),
            });

            if (!response.ok) {
                throw new Error(`API error: ${response.status}`);
            }

            const data = await response.json();
            setAccessCode(data);

            // 顯示成功通知
            setNotification({
                show: true,
                message: "Access code generated successfully!",
                type: "success"
            });
        } catch (err) {
            console.error("Error generating access code:", err);
            setNotification({
                show: true,
                message: `Error generating access code: ${err.message}`,
                type: "error"
            });
        } finally {
            setAccessCodeLoading(false);
        }
    };

    // 獲取激活客戶列表
    const fetchActivatedCustomers = async (filters = {}) => {
        if (!orgData) return;

        setCustomersLoading(true);
        try {
            // 構建查詢參數
            const queryParams = new URLSearchParams({
                orgId: orgData.orgId,
                ...filters
            });

            const response = await fetch(`https://h6ejj34g98.execute-api.ap-southeast-1.amazonaws.com/getActivatedCustomers?${queryParams.toString()}`);

            if (!response.ok) {
                throw new Error(`API error: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                // 按最後活動時間排序（從新到舊）
                const sortedCustomers = data.customers.sort((a, b) =>
                    new Date(b.lastActiveAt) - new Date(a.lastActiveAt)
                );
                setActivatedCustomers(sortedCustomers);
            } else {
                throw new Error(data.message || 'Failed to fetch activated customers');
            }
        } catch (err) {
            console.error("Error fetching activated customers:", err);
            setNotification({
                show: true,
                message: `Error: ${err.message}`,
                type: 'error'
            });
        } finally {
            setCustomersLoading(false);
        }
    };

    // 處理過濾器變更
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setCustomerFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // 應用過濾器
    const applyFilters = () => {
        // 只包含有值的過濾器
        const activeFilters = Object.entries(customerFilters)
            .reduce((acc, [key, value]) => {
                if (value) acc[key] = value;
                return acc;
            }, {});

        fetchActivatedCustomers(activeFilters);
    };

    // 重置過濾器
    const resetFilters = () => {
        setCustomerFilters({
            status: '',
            therapistName: '',
            treatmentType: '',
            startDate: '',
            endDate: ''
        });
        fetchActivatedCustomers();
    };

    // 切换侧边栏菜单状态
    const toggleMenu = () => {
        setMenuOpen(!menuOpen);
    };

    // 當組織數據加載完成後，獲取激活客戶列表
    useEffect(() => {
        if (orgData && activeTab === 'customers') {
            fetchActivatedCustomers();
        }
    }, [orgData, activeTab]);

    // 点击外部关闭菜单
    useEffect(() => {
        function handleClickOutside(event) {
            if (menuOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
                setMenuOpen(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [menuOpen]);

    const [selectedTreatmentId, setSelectedTreatmentId] = useState(null);
    const [selectedTreatmentNew, setSelectedTreatmentNew] = useState(null);
    const [deviceMappings, setDeviceMappings] = useState({});
    const [deviceInfo, setDeviceInfo] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [reportLoading, setReportLoading] = useState(false);

    // Load device mappings from Google Sheets when the component mounts
    useEffect(() => {
        if (activeTab === "reports") {
            fetchDeviceMappings();
        }
    }, [activeTab]);

    // 獲取 Google Sheets 數據
    const fetchGoogleSheetData = async () => {
        try {
            const response = await axios.get(
                `https://docs.google.com/spreadsheets/d/1QoK-tg6IVb5112-wpt3DUmoEnbk4N3miAqTlPNG_kkY/gviz/tq?tqx=out:json`
            );
            const text = response.data;

            // 清理返回的 JSON 數據
            const json = JSON.parse(text.substr(47).slice(0, -2));
            const rows = json.table.rows.map((row) =>
                row.c.map((cell) => (cell ? cell.v : ""))
            );

            // 確保我們有 orgId
            if (!orgData || !orgData.orgId) {
                console.error("No orgId available to filter device mappings");
                return {};
            }

            // 過濾符合當前 orgId 的資料行
            const orgRows = rows.filter(row => row[1] === orgData.orgId);
            console.log(`Filtered rows for orgId ${orgData.orgId}:`, orgRows);

            // 構建房間號碼到設備信息的映射
            const mappings = {};
            orgRows.forEach((row, index) => {
                const serialNumber = row[0]; // 序列號在第 0 列
                const orgId = row[1];        // orgId 在第 1 列
                const roomNumber = row[2];   // 房間號碼在第 2 列
                const macAddress = row[5];   // MAC 地址在第 5 列

                console.log(`OrgRow ${index}:`, { orgId, roomNumber, serialNumber, macAddress });

                if (roomNumber && macAddress) {
                    mappings[roomNumber] = {
                        serialNumber: serialNumber || 'N/A',
                        macAddress: macAddress || 'N/A'
                    };
                }
            });

            console.log(`Final device mappings for orgId ${orgData.orgId}:`, mappings);
            return mappings;
        } catch (err) {
            console.error("Failed to fetch Google Sheet data:", err.message);
            return {};
        }
    };

    // Fetch device mappings from Google Sheets
    const fetchDeviceMappings = async () => {
        if (orgData && orgData.orgId) {
            const mappings = await fetchGoogleSheetData();
            setDeviceMappings(mappings);
            console.log("Device mappings set to state:", mappings);
        } else {
            console.log("Cannot fetch device mappings: No orgId available");
        }
    };

    // Handle treatment selection for reports
    const handleTreatmentSelect = (treatmentId) => {
        setSelectedTreatmentId(treatmentId);
        setReportData(null); // 清除任何先前的報告數據

        if (!treatmentId) {
            setSelectedTreatment(null);
            setDeviceInfo(null);
            return;
        }

        // Find the selected treatment
        const treatment = treatments.find(t => t.treatmentId === treatmentId);
        setSelectedTreatment(treatment);

        // Check if we have device info for this room
        if (treatment && treatment.roomNumber && deviceMappings[treatment.roomNumber]) {
            setDeviceInfo(deviceMappings[treatment.roomNumber]);
        } else {
            setDeviceInfo(null);
            // 如果沒有設備信息，清空報告數據
            setReportData(null);
            // 可以選擇在這裡顯示通知
            if (treatment && treatment.roomNumber) {
                setNotification({
                    show: true,
                    message: `No device mapping found for room: ${treatment.roomNumber}`,
                    type: "warning"
                });
            }
        }
    };






    // 將API時間格式轉換為可讀時間
    const formatAPITimeToReadable = (apiTimeStr) => {
        // API時間格式: YYYYMMDDHHmmss
        const year = apiTimeStr.substring(0, 4);
        const month = apiTimeStr.substring(4, 6);
        const day = apiTimeStr.substring(6, 8);
        const hour = apiTimeStr.substring(8, 10);
        const minute = apiTimeStr.substring(10, 12);
        const second = apiTimeStr.substring(12, 14);

        return `${year}-${month}-${day} ${hour}:${minute}:${second} (HKT)`;
    };

    // 使用字符串比較處理時間序列數據
    const processTimeSeriesDataByStringCompare = (dataArray, startTimeStr, endTimeStr) => {
        console.log(`Processing with string comparison. Start: ${startTimeStr}, End: ${endTimeStr}`);
        console.log(`Data array length: ${dataArray.length}`);

        // 如果有數據，輸出一些示例幫助調試
        if (dataArray.length > 0) {
            console.log(`Sample time strings: ${dataArray[0].time}, ${dataArray[Math.floor(dataArray.length / 2)].time}, ${dataArray[dataArray.length - 1].time}`);
        }

        // 過濾出指定時間範圍內的數據，使用字符串比較
        const filteredData = dataArray.filter(item => {
            const timeStr = item.time;
            return timeStr >= startTimeStr && timeStr <= endTimeStr;
        });

        console.log(`Filtered data count using string compare: ${filteredData.length}`);

        // 如果過濾後沒有數據，使用寬鬆的過濾策略
        if (filteredData.length === 0) {
            console.log("No data after filtering. Using looser filtering criteria...");

            // 只使用時間部分進行過濾（忽略日期部分）
            const startTimeOnly = startTimeStr.substring(8, 12); // 取HHmm部分
            const endTimeOnly = endTimeStr.substring(8, 12);   // 取HHmm部分

            console.log(`Filtering by time only: ${startTimeOnly} to ${endTimeOnly}`);

            const timeFilteredData = dataArray.filter(item => {
                const itemTimeOnly = item.time.substring(8, 12);
                return itemTimeOnly >= startTimeOnly && itemTimeOnly <= endTimeOnly;
            });

            console.log(`Data points filtered by time only: ${timeFilteredData.length}`);

            if (timeFilteredData.length > 0) {
                return processDataArray(timeFilteredData);
            }

            // 如果依然沒有數據，則嘗試提供當天的所有數據
            const dayPart = startTimeStr.substring(0, 8);
            const dayFilteredData = dataArray.filter(item => item.time.startsWith(dayPart));

            console.log(`Data points for day ${dayPart}: ${dayFilteredData.length}`);

            if (dayFilteredData.length > 0) {
                return processDataArray(dayFilteredData);
            }

            // 最後手段：提供所有數據
            return processDataArray(dataArray);
        }

        return processDataArray(filteredData);
    };

    // 修改 processDataArray 函數來返回適合UI顯示的數據格式
    const processDataArray = (dataArray) => {
        if (dataArray.length === 0) {
            return {
                timePoints: [],
                values: [],
                stats: { avg: 0, min: 0, max: 0 }
            };
        }

        // 按時間排序
        dataArray.sort((a, b) => a.time.localeCompare(b.time));

        // 提取數值和格式化時間點
        const values = dataArray.map(item => item.value);
        const timePoints = dataArray.map(item => {
            // 從API時間字符串格式化為HH:mm
            const hour = item.time.substring(8, 10);
            const minute = item.time.substring(10, 12);
            return `${hour}:${minute}`;
        });

        // 計算統計值
        const sum = values.reduce((acc, val) => acc + val, 0);
        const avg = Math.round((sum / values.length) * 10) / 10;
        const min = Math.min(...values);
        const max = Math.max(...values);

        return {
            timePoints: timePoints,
            values: values,
            stats: { avg, min, max }
        };
    };

    // 計算放鬆評分的函數
    const calculateRelaxationScore = (heartRateStats, respiratoryRateStats) => {
        // 簡單的算法，可以根據實際需求調整
        // 理想的心率範圍：60-80
        // 理想的呼吸率範圍：12-16

        let score = 75; // 基礎分數

        // 心率評分調整
        const hr = heartRateStats.avg;
        if (hr < 60) score += 10; // 非常放鬆
        else if (hr <= 70) score += 5; // 很放鬆
        else if (hr <= 80) score += 0; // 正常
        else if (hr <= 90) score -= 5; // 稍微緊張
        else score -= 15; // 緊張

        // 呼吸率評分調整
        const rr = respiratoryRateStats.avg;
        if (rr < 12) score += 15; // 非常放鬆的呼吸
        else if (rr <= 14) score += 10; // 放鬆的呼吸
        else if (rr <= 16) score += 5; // 正常的呼吸
        else if (rr <= 18) score -= 5; // 稍快的呼吸
        else score -= 10; // 快速呼吸

        // 確保分數在0-100範圍內
        return Math.min(100, Math.max(0, Math.round(score)));
    };

    const handleExportPDF = async () => {
        try {
          // Set loading state
          setIsPdfExporting(true);
          
          // Show notification
          setNotification({
            show: true,
            message: "Generating PDF report...",
            type: "info"
          });
          
          // Get patient name
          const patientName = selectedTreatment ? 
            `${selectedTreatment.guestFirstName} ${selectedTreatment.guestLastName}` : 
            'Guest';
          
          // Get session date
          const sessionDate = selectedTreatment?.startTime ? 
            new Date(selectedTreatment.startTime) : 
            new Date();
          
          // Call the PDF generator
          await generateRelaxationReportPDF(
            reportData, 
            patientName, 
            sessionDate,
            selectedTreatment
          );
          
          // Show success notification
          setNotification({
            show: true,
            message: "PDF report exported successfully",
            type: "success"
          });
        } catch (error) {
          console.error("Error exporting PDF:", error);
          
          // Show error notification
          setNotification({
            show: true,
            message: `Error exporting PDF: ${error.message}`,
            type: "error"
          });
        } finally {
          // Reset loading state
          setIsPdfExporting(false);
        }
      };

    const exportReportPdf = () => {
        if (!reportData || !selectedTreatment) {
            setNotification({
                show: true,
                message: "No report data to export",
                type: "error"
            });
            return;
        }

        setNotification({
            show: true,
            message: "Generating PDF report...",
            type: "info"
        });

        try {
            // 創建PDF
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });

            // 設置字體和顏色
            pdf.setFont("helvetica");
            pdf.setTextColor(109, 76, 65); // #6d4c41

            // 添加標題
            pdf.setFontSize(24);
            pdf.text("Relaxation Report", 105, 20, { align: 'center' });

            // 添加報告日期
            pdf.setFontSize(12);
            pdf.text(`Report Date: ${formatDate(new Date())}`, 105, 30, { align: 'center' });

            // 添加分隔線
            pdf.setDrawColor(141, 110, 99); // #8d6e63
            pdf.line(20, 35, 190, 35);

            // 添加客戶資訊
            pdf.setFontSize(14);
            pdf.text("Client Information", 20, 45);

            pdf.setFontSize(12);
            pdf.text(`Name: ${selectedTreatment.guestFirstName} ${selectedTreatment.guestLastName}`, 20, 55);
            pdf.text(`Treatment: ${selectedTreatment.treatmentType}`, 20, 62);
            pdf.text(`Duration: ${selectedTreatment.duration} minutes`, 20, 69);
            pdf.text(`Date: ${formatDate(selectedTreatment.startTime)}`, 20, 76);
            pdf.text(`Time: ${formatTime(selectedTreatment.startTime)}`, 20, 83);
            pdf.text(`Room: ${selectedTreatment.roomNumber}`, 20, 90);

            // 添加分隔線
            pdf.line(20, 95, 190, 95);

            // 添加報告概述
            pdf.setFontSize(14);
            pdf.text("Summary", 20, 105);

            // 添加關鍵指標
            pdf.setFontSize(12);
            pdf.text(`Total Relaxation Time: ${reportData.totalSleep} minutes`, 20, 115);
            pdf.text(`Average Heart Rate: ${reportData.avgHr} BPM`, 20, 122);
            pdf.text(`Average Respiration Rate: ${reportData.avgRr} breaths/min`, 20, 129);
            pdf.text(`Relaxation Score: ${reportData.score}/100`, 20, 136);

            // 添加分隔線
            pdf.line(20, 145, 190, 145);

            // 添加詳細分析
            pdf.setFontSize(14);
            pdf.text("Detailed Analysis", 20, 155);

            // 添加心率描述
            pdf.setFontSize(12);
            pdf.text("Heart Rate:", 20, 165);

            // 根據心率生成描述
            let hrDescription = getHeartRateDescription(reportData.avgHr);

            // 添加文本換行
            const hrLines = pdf.splitTextToSize(hrDescription, 160);
            pdf.text(hrLines, 30, 172);

            // 添加呼吸率描述
            pdf.text("Respiration Rate:", 20, 185);

            // 根據呼吸率生成描述
            let rrDescription = getRespirationDescription(reportData.avgRr);

            // 添加文本換行
            const rrLines = pdf.splitTextToSize(rrDescription, 160);
            pdf.text(rrLines, 30, 192);

            // 添加整體評分描述
            pdf.text("Overall Relaxation:", 20, 205);

            // 根據分數生成描述
            let scoreDescription = getScoreDescription(reportData.score);

            // 添加文本換行
            const scoreLines = pdf.splitTextToSize(scoreDescription, 160);
            pdf.text(scoreLines, 30, 212);

            // 添加建議
            pdf.text("Recommendations:", 20, 230);

            // 生成建議文本
            let recommendation = getRecommendation(reportData, selectedTreatment);

            // 添加文本換行
            const recLines = pdf.splitTextToSize(recommendation, 160);
            pdf.text(recLines, 30, 237);

            // 添加頁腳
            pdf.setFontSize(10);
            pdf.setTextColor(150, 150, 150); // 灰色
            pdf.text(`Report Issued on ${new Date().toLocaleString()}`, 105, 287, { align: 'center' });

            // 創建下載文件名
            const guestName = selectedTreatment.guestFirstName.replace(/\s/g, '_');
            const dateStr = formatDate(selectedTreatment.startTime).replace(/\//g, '-');
            const fileName = `Relaxation_Report_${guestName}_${dateStr}.pdf`;

            // 下載 PDF
            pdf.save(fileName);

            setNotification({
                show: true,
                message: "PDF exported successfully",
                type: "success"
            });
        } catch (error) {
            console.error("Error generating PDF:", error);
            setNotification({
                show: true,
                message: `Error generating PDF: ${error.message}`,
                type: "error"
            });
        }
    };

    // Share report
    const shareReport = () => {
        // Placeholder for share functionality
        setNotification({
            show: true,
            message: "Report sharing feature coming soon",
            type: "info"
        });
    };

    // 示例放鬆報告數據 - 僅包含API返回的實際數據欄位
    const sampleReportData = {
        totalSleep: 57,   // 總放鬆時間（分鐘）
        avgHr: 68,        // 平均心率（次/分鐘）
        avgRr: 14,        // 平均呼吸率（次/分鐘）
        score: 85         // 整體放鬆評分
    };

    // 示例治療數據
    const sampleTreatment = {
        treatmentId: "sample001",
        guestFirstName: "Emma",
        guestLastName: "Johnson",
        treatmentType: "Relaxation Massage",
        duration: 60,
        startTime: new Date().toISOString(),
        roomNumber: "Lavandra",
        therapistName: "Grace Marasigan"
    };

    // 根據評分獲取顏色
    const getScoreColor = (score) => {
        if (score >= 90) return "#4CAF50"; // 出色 - 綠色
        if (score >= 75) return "#8BC34A"; // 良好 - 淺綠色
        if (score >= 60) return "#FFC107"; // 一般 - 黃色
        if (score >= 40) return "#FF9800"; // 較差 - 橙色
        return "#F44336"; // 不佳 - 紅色
    };

    // 根據評分獲取描述
    const getScoreDescription = (score) => {
        if (score >= 90) return "Excellent relaxation achieved. The guest reached a deep state of calm during the treatment.";
        if (score >= 75) return "Good relaxation level. The guest experienced a substantial calming effect from the treatment.";
        if (score >= 60) return "Moderate relaxation. The guest showed signs of relaxation but may benefit from enhanced techniques.";
        if (score >= 40) return "Minimal relaxation. The guest may have had difficulty fully relaxing during the treatment.";
        return "Limited relaxation detected. The guest may have been experiencing stress or discomfort.";
    };

    // 獲取心率描述
    const getHeartRateDescription = (hr) => {
        if (hr < 60) return "Excellent relaxation heart rate, indicating deep calm.";
        if (hr < 70) return "Good relaxation heart rate, showing effective calming response.";
        if (hr < 80) return "Moderate heart rate, showing some relaxation effect.";
        if (hr < 90) return "Slightly elevated heart rate, indicating limited relaxation.";
        return "Elevated heart rate, suggesting minimal relaxation achieved.";
    };

    // 獲取心率顏色
    const getHeartRateColor = (hr) => {
        if (hr < 60) return "#4CAF50"; // 綠色
        if (hr < 70) return "#8BC34A"; // 淺綠色
        if (hr < 80) return "#FFC107"; // 黃色
        if (hr < 90) return "#FF9800"; // 橙色
        return "#F44336"; // 紅色
    };

    // 獲取呼吸率描述
    const getRespirationDescription = (rr) => {
        if (rr < 12) return "Deep, slow breathing indicating excellent relaxation.";
        if (rr < 14) return "Relaxed breathing pattern showing good calming response.";
        if (rr < 16) return "Normal relaxed breathing rate.";
        if (rr < 18) return "Slightly elevated breathing rate showing moderate relaxation.";
        return "Higher than optimal breathing rate, indicating limited relaxation.";
    };

    // 獲取呼吸率顏色
    const getRespirationColor = (rr) => {
        if (rr < 12) return "#4CAF50"; // 綠色
        if (rr < 14) return "#8BC34A"; // 淺綠色
        if (rr < 16) return "#FFC107"; // 黃色
        if (rr < 18) return "#FF9800"; // 橙色
        return "#F44336"; // 紅色
    };

    // 獲取針對性建議
    const getRecommendation = (data, treatment) => {
        if (data.score >= 85) {
            return `${treatment.guestFirstName} responded exceptionally well to this ${treatment.treatmentType}. Consider recommending similar treatments or extending session duration for future visits.`;
        } else if (data.score >= 70) {
            return `Good response to the treatment. Consider customizing future ${treatment.treatmentType} sessions with additional focus on areas that enhance relaxation.`;
        } else {
            return `For improved relaxation, consider suggesting alternative treatment types or modified techniques that may better suit ${treatment.guestFirstName}'s relaxation needs.`;
        }
    };

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <div style={styles.loadingSpinner}></div>
                <p style={styles.loadingText}>Loading organization data...</p>
            </div>
        );
    }

    // 檢查orgData是否存在
    if (!orgData) {
        return (
            <div style={styles.errorContainer}>
                <h2 style={styles.errorTitle}>Organization Not Found</h2>
                <p style={styles.errorText}>Unable to load organization data. Please try again.</p>
                <button
                    style={styles.errorButton}
                    onClick={() => navigate("/org/login")}
                >
                    Return to Login
                </button>
            </div>
        );
    }

    // Generate sleep report
    const generateSleepReport = async () => {
        if (!selectedTreatment || !deviceInfo || !deviceInfo.macAddress) {
            // 如果沒有設備信息，清空報告數據並顯示錯誤
            setReportData(null); // 清空報告數據
            setNotification({
                show: true,
                message: "Cannot generate report: Missing device information",
                type: "error"
            });
            return;
        }

        setReportLoading(true);
        try {
            // 從treatment日期中提取YYYYMMDD格式的日期
            const treatmentDate = new Date(selectedTreatment.startTime);
            const formattedDate = treatmentDate.toISOString().slice(0, 10).replace(/-/g, '');

            console.log(`Generating report for MAC: ${deviceInfo.macAddress}, Date: ${formattedDate}`);
            console.log(`Treatment start time: ${selectedTreatment.startTime}`);
            console.log(`Treatment end time: ${selectedTreatment.endTime || 'Not specified'}`);
            console.log(`Treatment duration: ${selectedTreatment.duration || 'Not specified'}`);

            // 使用新的API端點
            const response = await fetch('https://aisx6r0ac1.execute-api.ap-southeast-1.amazonaws.com/dev/api', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    mac: deviceInfo.macAddress,
                    date: formattedDate
                }),
            });

            if (!response.ok) {
                throw new Error(`API error: ${response.status}`);
            }

            const responseData = await response.json();

            // 解析API響應
            if (responseData.statusCode === 200 && responseData.body) {
                // 解析body字符串（如果是字符串格式）
                const bodyData = typeof responseData.body === 'string'
                    ? JSON.parse(responseData.body)
                    : responseData.body;

                // 檢查業務響應代碼
                if (bodyData.code === "0000" && bodyData.data) {
                    // 提取出原始的呼吸率和心率數據
                    const { rrData, hrData } = bodyData.data;

                    console.log(`RR Data arrays: ${rrData.length}, HR Data arrays: ${hrData.length}`);

                    // 分析API返回的數據範圍
                    const allRRData = rrData.flat();
                    const allHRData = hrData.flat();

                    if (allRRData.length > 0) {
                        // 找出API數據的最早和最晚時間
                        const firstDataTime = allRRData[0].time;
                        const lastDataTime = allRRData[allRRData.length - 1].time;

                        console.log(`API data time range: ${firstDataTime} to ${lastDataTime}`);
                        console.log(`API data readable time range: ${formatAPITimeToReadable(firstDataTime)} to ${formatAPITimeToReadable(lastDataTime)}`);

                        // 抽取API數據的日期部分（YYYYMMDD）
                        const apiDatePart = firstDataTime.substring(0, 8);
                        console.log(`API data date part: ${apiDatePart}`);
                    }

                    // 計算實際的治療時間範圍 - 使用當地時間
                    let treatmentStartTime = new Date(selectedTreatment.startTime);
                    let treatmentEndTime;

                    if (selectedTreatment.endTime) {
                        treatmentEndTime = new Date(selectedTreatment.endTime);
                    } else if (selectedTreatment.duration) {
                        // 使用持續時間計算結束時間
                        treatmentEndTime = new Date(treatmentStartTime.getTime() + selectedTreatment.duration * 60000);
                    } else {
                        // 默認為開始時間後1小時
                        treatmentEndTime = new Date(treatmentStartTime.getTime() + 60 * 60 * 1000);
                    }

                    console.log(`Local treatment time: ${treatmentStartTime.toLocaleString()} to ${treatmentEndTime.toLocaleString()}`);

                    // 從治療時間提取小時和分鐘
                    const startHour = treatmentStartTime.getHours();
                    const startMinute = treatmentStartTime.getMinutes();
                    const endHour = treatmentEndTime.getHours();
                    const endMinute = treatmentEndTime.getMinutes();

                    console.log(`Treatment hours: ${startHour}:${startMinute} to ${endHour}:${endMinute}`);

                    // 直接使用API數據的日期部分，結合治療的時間部分
                    const apiDatePart = allRRData.length > 0 ? allRRData[0].time.substring(0, 8) : formattedDate;

                    // 構建新的時間範圍字符串，格式：YYYYMMDDHHmmss
                    const treatmentStartStr = `${apiDatePart}${String(startHour).padStart(2, '0')}${String(startMinute).padStart(2, '0')}00`;
                    const treatmentEndStr = `${apiDatePart}${String(endHour).padStart(2, '0')}${String(endMinute).padStart(2, '0')}00`;

                    console.log(`Adjusted treatment time in API format: ${treatmentStartStr} to ${treatmentEndStr}`);
                    console.log(`Readable adjusted treatment time: ${formatAPITimeToReadable(treatmentStartStr)} to ${formatAPITimeToReadable(treatmentEndStr)}`);

                    // 處理數據
                    const processedRRData = processTimeSeriesDataByStringCompare(allRRData, treatmentStartStr, treatmentEndStr);
                    const processedHRData = processTimeSeriesDataByStringCompare(allHRData, treatmentStartStr, treatmentEndStr);

                    // 创建原始数据结构
                    const processedData = {
                        respiratoryRate: processedRRData,
                        heartRate: processedHRData
                    };

                    // 转换为报告UI所需的格式
                    const formattedReportData = {
                        // 总治疗时间（分钟），如果没有用治疗持续时间
                        totalSleep: selectedTreatment.duration || 60,
                        // 平均心率
                        avgHr: processedHRData.stats.avg,
                        // 平均呼吸率
                        avgRr: processedRRData.stats.avg,
                        // 计算放松评分
                        score: calculateRelaxationScore(processedHRData.stats, processedRRData.stats),
                        // 保留原始数据 - 确保添加这个字段
                        rawData: {
                            respiratoryRate: processedRRData,
                            heartRate: processedHRData
                        },
                        // Add timestamp for PDF export
                        timestamp: treatmentStartTime
                    };

                    console.log("Setting report data:", formattedReportData);
                    setReportData(formattedReportData);

                    setNotification({
                        show: true,
                        message: "Sleep report generated successfully",
                        type: "success"
                    });
                } else {
                    // 業務錯誤，清空報告
                    setReportData(null);
                    throw new Error(bodyData.msg || "API response error");
                }
            } else {
                // API響應錯誤，清空報告
                setReportData(null);
                throw new Error("Invalid API response format");
            }
        } catch (err) {
            // 捕獲到錯誤，清空報告數據
            console.error("Error generating sleep report:", err);
            setReportData(null); // 清空報告數據，確保之前的報告不會顯示
            setNotification({
                show: true,
                message: `Error: ${err.message}`,
                type: "error"
            });
        } finally {
            setReportLoading(false);
        }
    };


    return (
        <div style={styles.container}>
            {/* 移动菜单按钮 */}
            <div
                className={`menu-toggle ${menuOpen ? "menu-open" : ""}`}
                onClick={() => setMenuOpen(!menuOpen)}
            >
                <span></span>
                <span></span>
                <span></span>
            </div>
            {/* 背景遮罩 */}
            <div
                className={`sidebar-backdrop ${menuOpen ? "show" : ""}`}
                onClick={() => setMenuOpen(false)}
            ></div>
            {/* 侧边栏 */}
            <div
                ref={sidebarRef}
                style={styles.sidebar}
                className={`sidebar ${menuOpen ? "sidebar-open" : ""}`}
            >
                <div style={styles.logoContainer}>
                    <span style={styles.poweredByText}>Powered by</span>
                    <img src={katlaiLogo} alt="KATLAI Logo" style={styles.logoImage} />
                </div>

                <div style={styles.menuContainer}>
                    <div
                        style={activeTab === "dashboard" ? { ...styles.menuItem, ...styles.activeMenuItem } : styles.menuItem}
                        onClick={() => { setActiveTab("dashboard"); setMenuOpen(false); }}
                    >
                        <span style={styles.menuIcon}>📊</span>
                        <span style={styles.menuText}>Dashboard</span>
                    </div>

                    <div
                        style={activeTab === "treatments" ? { ...styles.menuItem, ...styles.activeMenuItem } : styles.menuItem}
                        onClick={() => { setActiveTab("treatments"); setMenuOpen(false); }}
                    >
                        <span style={styles.menuIcon}>💆</span>
                        <span style={styles.menuText}>Treatments</span>
                    </div>

                    <div
                        style={activeTab === "access" ? { ...styles.menuItem, ...styles.activeMenuItem } : styles.menuItem}
                        onClick={() => { setActiveTab("access"); setMenuOpen(false); }}
                    >
                        <span style={styles.menuIcon}>🔑</span>
                        <span style={styles.menuText}>Access Codes</span>
                    </div>

                    <div
                        style={activeTab === "customers" ? { ...styles.menuItem, ...styles.activeMenuItem } : styles.menuItem}
                        onClick={() => { setActiveTab("customers"); setMenuOpen(false); }}
                    >
                        <span style={styles.menuIcon}>👥</span>
                        <span style={styles.menuText}>Activated Customers</span>
                    </div>

                    <div
                        style={activeTab === "reports" ? { ...styles.menuItem, ...styles.activeMenuItem } : styles.menuItem}
                        onClick={() => { setActiveTab("reports"); setMenuOpen(false); }}
                    >
                        <span style={styles.menuIcon}>📈</span>
                        <span style={styles.menuText}>Reports</span>
                    </div>

                    <div
                        style={activeTab === "settings" ? { ...styles.menuItem, ...styles.activeMenuItem } : styles.menuItem}
                        onClick={() => { setActiveTab("settings"); setMenuOpen(false); }}
                    >
                        <span style={styles.menuIcon}>⚙️</span>
                        <span style={styles.menuText}>Settings</span>
                    </div>
                </div>

                <div style={styles.logoutContainer}>
                    <div style={styles.menuItem} onClick={handleLogout}>
                        <span style={styles.menuIcon}>🚪</span>
                        <span style={styles.menuText}>Logout</span>
                    </div>
                </div>
            </div>

            <div style={styles.mainContent}>
                <div style={styles.header}>
                    <div style={styles.pageTitle}>
                        <h1 style={styles.organizationName}>{orgData.orgName}</h1>
                        <p style={styles.welcomeText}>Welcome back, {orgData.adminName}</p>
                    </div>

                    <div style={styles.userInfo}>
                        <div style={styles.refreshButton} onClick={handleRefreshData} disabled={refreshing}>
                            <span style={refreshing ? { ...styles.refreshIcon, ...styles.refreshingSpin } : styles.refreshIcon}>↻</span>
                            <span style={styles.refreshText}>{refreshing ? "Refreshing..." : "Refresh Data"}</span>
                        </div>
                        {/* <div style={styles.notification}>
                            <span style={styles.notificationIcon}>🔔</span>
                            <span style={styles.notificationBadge}>3</span>
                        </div> */}
                        <div style={styles.userProfile}>
                            <div style={styles.userAvatar}>{orgData.adminName.charAt(0)}</div>
                            <div style={styles.userDetails}>
                                <p style={styles.userName}>{orgData.adminName}</p>
                                <p style={styles.userRole}>Administrator</p>
                            </div>
                        </div>
                    </div>
                </div>

                {activeTab === "dashboard" && (
                    <div style={styles.dashboardContent}>
                        <div style={styles.statCards}>
                            <div style={styles.statCard}>
                                <h3 style={styles.statTitle}>Active Sessions</h3>
                                {statisticsLoading ? (
                                    <p style={styles.loadingText}>Loading...</p>
                                ) : (
                                    <>
                                        <p style={styles.statValue}>{dashboardData.activeSessions}</p>
                                        <p style={styles.statInfo}>Current active treatments</p>
                                    </>
                                )}
                            </div>

                            <div style={styles.statCard}>
                                <h3 style={styles.statTitle}>Total Treatments</h3>
                                {statisticsLoading ? (
                                    <p style={styles.loadingText}>Loading...</p>
                                ) : (
                                    <>
                                        <p style={styles.statValue}>{dashboardData.totalTreatments}</p>
                                        <p style={styles.statInfo}>Lifetime treatments</p>
                                    </>
                                )}
                            </div>

                            <div style={styles.statCard}>
                                <h3 style={styles.statTitle}>Today's Treatments</h3>
                                {statisticsLoading ? (
                                    <p style={styles.loadingText}>Loading...</p>
                                ) : (
                                    <>
                                        <p style={styles.statValue}>{dashboardData.todaysTreatments}</p>
                                        <p style={styles.statInfo}>Scheduled for today</p>
                                    </>
                                )}
                            </div>

                            <div style={styles.statCard}>
                                <h3 style={styles.statTitle}>Upcoming Bookings</h3>
                                {statisticsLoading ? (
                                    <p style={styles.loadingText}>Loading...</p>
                                ) : (
                                    <>
                                        <p style={styles.statValue}>{dashboardData.upcomingBookings}</p>
                                        <p style={styles.statInfo}>Next 24 hours</p>
                                    </>
                                )}
                            </div>
                        </div>

                        <div style={styles.treatmentsSection}>
                            <div style={styles.sectionHeader}>
                                <h2 style={styles.sectionTitle}>Recent Treatments</h2>
                                <button
                                    style={styles.actionButton}
                                    onClick={() => setShowTreatmentModal(true)}
                                >
                                    <span style={styles.actionIcon}>➕</span>
                                    <span style={styles.actionText}>New Treatment</span>
                                </button>
                            </div>

                            {treatmentsLoading ? (
                                <div style={styles.loadingContainer}>
                                    <div style={styles.loadingSpinner}></div>
                                    <p style={styles.loadingText}>Loading treatments...</p>
                                </div>
                            ) : treatments.length === 0 ? (
                                <div style={styles.emptyState}>
                                    <p style={styles.emptyText}>No treatments found</p>
                                    <button
                                        style={styles.actionButton}
                                        onClick={() => setShowTreatmentModal(true)}
                                    >
                                        Create your first treatment
                                    </button>
                                </div>
                            ) : (
                                <>
                                    {/* 桌面版表格顯示 */}
                                    <div className="desktop-view">
                                        <div style={styles.treatmentList}>
                                            <div style={styles.treatmentHeader}>
                                                <span style={styles.treatmentHeaderItem}>Date & Time</span>
                                                <span style={styles.treatmentHeaderItem}>Guest</span>
                                                <span style={styles.treatmentHeaderItem}>Treatment</span>
                                                <span style={styles.treatmentHeaderItem}>Therapist</span>
                                                <span style={styles.treatmentHeaderItem}>Room</span>
                                                <span style={styles.treatmentHeaderItem}>Status</span>
                                            </div>
                                            {treatments.slice(0, 5).map((treatment, index) => (
                                                <div key={index} style={styles.treatmentItem}>
                                                    <div style={styles.treatmentInfo}>
                                                        <span style={styles.treatmentDate}>{formatDate(treatment.startTime)}</span>
                                                        <span style={styles.treatmentTime}>{formatTime(treatment.startTime)}</span>
                                                    </div>
                                                    <div style={styles.treatmentGuest}>
                                                        <span>{treatment.guestFirstName} {treatment.guestLastName}</span>
                                                    </div>
                                                    <div style={styles.treatmentType}>
                                                        <span>{treatment.treatmentType}</span>
                                                        <span style={styles.treatmentDuration}>{treatment.duration} min</span>
                                                    </div>
                                                    <div style={styles.therapistName}>
                                                        <span>{treatment.therapistName}</span>
                                                    </div>
                                                    <div style={styles.roomNumber}>
                                                        <span>{treatment.roomNumber}</span>
                                                    </div>
                                                    <div style={{ ...styles.treatmentStatus, ...getStatusStyle(treatment.status) }}>
                                                        <span>{formatStatus(treatment.status)}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* 移動端卡片顯示 */}
                                    <div className="mobile-view">
                                        {treatments.slice(0, 5).map((treatment, index) => (
                                            <div key={index} className="treatment-card">
                                                <div className="card-header">
                                                    <div>
                                                        <div>{formatDate(treatment.startTime)}</div>
                                                        <div>{formatTime(treatment.startTime)}</div>
                                                    </div>
                                                    <div style={{
                                                        ...styles.statusBadge,
                                                        backgroundColor: getStatusColor(treatment.status)
                                                    }}>
                                                        {formatStatus(treatment.status)}
                                                    </div>
                                                </div>
                                                <div className="card-body">
                                                    <div className="card-row">
                                                        <span className="card-label">Guest:</span>
                                                        <span>{treatment.guestFirstName} {treatment.guestLastName}</span>
                                                    </div>
                                                    <div className="card-row">
                                                        <span className="card-label">Treatment:</span>
                                                        <span>{treatment.treatmentType}</span>
                                                    </div>
                                                    <div className="card-row">
                                                        <span className="card-label">Therapist:</span>
                                                        <span>{treatment.therapistName}</span>
                                                    </div>
                                                    <div className="card-row">
                                                        <span className="card-label">Room:</span>
                                                        <span>{treatment.roomNumber}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}

                            {treatments.length > 5 && (
                                <div style={styles.viewMoreContainer}>
                                    <button
                                        style={styles.viewMoreButton}
                                        onClick={() => setActiveTab("treatments")}
                                    >
                                        View All Treatments
                                    </button>
                                </div>
                            )}
                        </div>

                        <div style={styles.recentActivity}>
                            <h2 style={styles.sectionTitle}>Recent Activity</h2>
                            {treatmentsLoading ? (
                                <div style={styles.loadingContainer}>
                                    <p style={styles.loadingText}>Loading recent activities...</p>
                                </div>
                            ) : dashboardData.recentActivity.length === 0 ? (
                                <p style={styles.emptyText}>No recent activities to display</p>
                            ) : (
                                <div style={styles.activityList}>
                                    {dashboardData.recentActivity.map((activity, index) => (
                                        <div key={index} style={styles.activityItem}>
                                            <div style={styles.activityTime}>{activity.time}</div>
                                            <div style={styles.activityDetails}>
                                                <p style={styles.activityAction}>{activity.action}</p>
                                                <p style={styles.activityInfo}>
                                                    {activity.room} • {activity.therapyType}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div style={styles.quickActions}>
                            <h2 style={styles.sectionTitle}>Quick Actions</h2>
                            <div style={styles.actionButtons}>
                                <button
                                    style={styles.actionButton}
                                    onClick={() => setShowTreatmentModal(true)}
                                >
                                    <span style={styles.actionIcon}>➕</span>
                                    <span style={styles.actionText}>New Treatment</span>
                                </button>

                                <button style={styles.actionButton} onClick={() => setActiveTab("reports")}>
                                    <span style={styles.actionIcon}>📝</span>
                                    <span style={styles.actionText}>Create Report</span>
                                </button>

                                <button style={styles.actionButton}>
                                    <span style={styles.actionIcon}>🔍</span>
                                    <span style={styles.actionText}>View Analytics</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === "treatments" && (
                    <div style={styles.treatmentsContainer}>
                        <div style={styles.sectionHeader}>
                            <h2 style={styles.sectionTitle}>All Treatments</h2>
                            <button
                                style={styles.actionButton}
                                onClick={() => setShowTreatmentModal(true)}
                            >
                                <span style={styles.actionIcon}>➕</span>
                                <span style={styles.actionText} className="hide-on-small">New Treatment</span>
                            </button>
                        </div>

                        {treatmentsLoading ? (
                            <div style={styles.loadingContainer}>
                                <div style={styles.loadingSpinner}></div>
                                <p>Loading treatments...</p>
                            </div>
                        ) : (
                            <>
                                {/* 桌面视图 - 表格 */}
                                <div className="desktop-view">
                                    <div style={styles.tableContainer}>
                                        <table style={styles.table}>
                                            <thead>
                                                <tr>
                                                    <th style={styles.tableHeader}>Date</th>
                                                    <th style={styles.tableHeader}>Time</th>
                                                    <th style={styles.tableHeader}>Guest</th>
                                                    <th style={styles.tableHeader}>Treatment</th>
                                                    <th style={styles.tableHeader}>Duration</th>
                                                    <th style={styles.tableHeader}>Therapist</th>
                                                    <th style={styles.tableHeader}>Room</th>
                                                    <th style={styles.tableHeader}>Status</th>
                                                    <th style={styles.tableHeader}>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {treatments.map((treatment, index) => (
                                                    <tr key={treatment.treatmentId || index} style={styles.tableRow}>
                                                        <td style={styles.tableCell}>{formatDate(treatment.startTime)}</td>
                                                        <td style={styles.tableCell}>{formatTime(treatment.startTime)}</td>
                                                        <td style={styles.tableCell}>{`${treatment.guestFirstName} ${treatment.guestLastName}`}</td>
                                                        <td style={styles.tableCell}>{treatment.treatmentType}</td>
                                                        <td style={styles.tableCell}>{`${treatment.duration} min`}</td>
                                                        <td style={styles.tableCell}>{treatment.therapistName}</td>
                                                        <td style={styles.tableCell}>{treatment.roomNumber}</td>
                                                        <td style={styles.tableCell}>
                                                            <span style={{
                                                                ...styles.statusBadge,
                                                                backgroundColor: getStatusColor(treatment.status)
                                                            }}>
                                                                {formatStatus(treatment.status)}
                                                            </span>
                                                        </td>
                                                        {/* 桌面版 Actions */}
                                                        <td style={styles.tableCell}>
                                                            <div style={styles.actionButtons}>
                                                                {/* Edit: 永遠顯示 */}
                                                                <button
                                                                    onClick={() => handleEditTreatment(treatment)}
                                                                    style={styles.editButton}
                                                                    title="Edit treatment"
                                                                >
                                                                    <span>✎</span>
                                                                </button>

                                                                {/* Permanent Delete: 永遠顯示（有管理權限） */}
                                                                {true && (
                                                                    <button
                                                                        onClick={() => handleDeletePrompt(treatment)}
                                                                        style={styles.deleteButton}
                                                                        title="Delete permanently"
                                                                    >
                                                                        <span>✕</span>
                                                                    </button>
                                                                )}

                                                                {/* Quick Remove: 只在 scheduled 顯示，且彈窗確認 */}
                                                                {treatment.status === 'scheduled' && (
                                                                    <button
                                                                        onClick={() => handleCancelPrompt(treatment)}
                                                                        style={styles.removeButton}
                                                                        title="Temporarily remove (cancel)"
                                                                    >
                                                                        <span>⊗</span>
                                                                    </button>
                                                                )}

                                                                {/* Restore: 只在 cancelled 顯示 */}
                                                                {treatment.status === 'cancelled' && (
                                                                    <button
                                                                        onClick={() => handleToggleTreatmentStatus(treatment)}
                                                                        style={styles.restoreButton}
                                                                        title="Restore to scheduled"
                                                                    >
                                                                        <span>↩</span>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* 移动端视图 - 卡片 */}
                                <div className="mobile-view">
                                    {treatments.map((treatment, index) => (
                                        <div key={treatment.treatmentId || index} className="treatment-card">
                                            <div className="card-header">
                                                <div>
                                                    <div>{formatDate(treatment.startTime)}</div>
                                                    <div>{formatTime(treatment.startTime)}</div>
                                                </div>
                                                <div style={{
                                                    ...styles.statusBadge,
                                                    backgroundColor: getStatusColor(treatment.status)
                                                }}>
                                                    {formatStatus(treatment.status)}
                                                </div>
                                            </div>
                                            <div className="card-body">
                                                <div className="card-row">
                                                    <span className="card-label">Guest:</span>
                                                    <span>{`${treatment.guestFirstName} ${treatment.guestLastName}`}</span>
                                                </div>
                                                <div className="card-row">
                                                    <span className="card-label">Treatment:</span>
                                                    <span>{treatment.treatmentType}</span>
                                                </div>
                                                <div className="card-row">
                                                    <span className="card-label">Duration:</span>
                                                    <span>{`${treatment.duration} min`}</span>
                                                </div>
                                                <div className="card-row">
                                                    <span className="card-label">Therapist:</span>
                                                    <span>{treatment.therapistName}</span>
                                                </div>
                                                <div className="card-row">
                                                    <span className="card-label">Room:</span>
                                                    <span>{treatment.roomNumber}</span>
                                                </div>
                                            </div>
                                            <div className="card-actions">
                                                <button
                                                    onClick={() => handleEditTreatment(treatment)}
                                                    className="btn edit-btn"
                                                    title="Edit treatment"
                                                >
                                                    <span>✎</span>
                                                </button>

                                                <button
                                                    onClick={() => handleDeletePrompt(treatment)}
                                                    className="btn delete-btn"
                                                    title="Delete permanently"
                                                >
                                                    <span>✕</span>
                                                </button>

                                                {(treatment.status === 'scheduled' || treatment.status === 'cancelled') && (
                                                    <button
                                                        onClick={() => handleToggleTreatmentStatus(treatment)}
                                                        className={treatment.status === 'cancelled' ? "btn restore-btn" : "btn remove-btn"}
                                                        title={treatment.status === 'cancelled' ? "Restore to scheduled" : "Remove treatment"}
                                                    >
                                                        <span>{treatment.status === 'cancelled' ? "↩" : "🗑"}</span>
                                                    </button>
                                                )}

                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}




                {activeTab === "access" && (
                    <div style={styles.accessCodesContainer}>
                        <h2 style={styles.sectionTitle}>Treatment Access Codes</h2>

                        <div style={styles.accessCodeIntro}>
                            <p>Generate access codes for guests to view their treatments and provide feedback.</p>
                        </div>

                        <div className="access-code-content-wrapper" style={styles.accessCodeContentWrapper}>
                            <div style={styles.treatmentSelectionPanel}>
                                <h3 style={styles.accessSubtitle}>Select a Treatment</h3>
                                {treatmentsLoading ? (
                                    <div style={styles.loadingContainer}>
                                        <div style={styles.loadingSpinner}></div>
                                        <p>Loading treatments...</p>
                                    </div>
                                ) : treatments.length === 0 ? (
                                    <p style={styles.emptyText}>No treatments available</p>
                                ) : (
                                    <div style={styles.treatmentListAccess}>
                                        {treatments.map((treatment, index) => (
                                            <div
                                                key={index}
                                                style={{
                                                    ...styles.accessTreatmentItem,
                                                    ...(selectedTreatment?.treatmentId === treatment.treatmentId ? styles.selectedTreatmentItem : {})
                                                }}
                                                onClick={() => {
                                                    // 當選擇新的治療時，清除之前的訪問碼
                                                    if (selectedTreatment?.treatmentId !== treatment.treatmentId) {
                                                        setAccessCode(null);
                                                    }
                                                    setSelectedTreatment(treatment);
                                                }}
                                            >
                                                <div style={styles.accessTreatmentHeader}>
                                                    <span style={styles.accessTreatmentDate}>{formatDate(treatment.startTime)}</span>
                                                    <span style={styles.accessTreatmentTime}>{formatTime(treatment.startTime)}</span>
                                                    <span style={{
                                                        ...styles.statusBadge,
                                                        backgroundColor: getStatusColor(treatment.status),
                                                        fontSize: '10px',
                                                        padding: '2px 6px'
                                                    }}>
                                                        {formatStatus(treatment.status)}
                                                    </span>
                                                </div>
                                                <div style={styles.accessTreatmentContent}>
                                                    <p style={styles.accessTreatmentGuest}>
                                                        <strong>Guest:</strong> {treatment.guestFirstName} {treatment.guestLastName}
                                                        {treatment.clientId && <span style={styles.clientIdBadge} title="Customer ID available">🆔</span>}
                                                    </p>
                                                    <p style={styles.accessTreatmentType}>
                                                        <strong>Treatment:</strong> {treatment.treatmentType}
                                                    </p>
                                                    <p style={styles.accessTreatmentDetails}>
                                                        <span><strong>Therapist:</strong> {treatment.therapistName}</span>
                                                        <span><strong>Room:</strong> {treatment.roomNumber}</span>
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div style={styles.accessCodePanel}>
                                <h3 style={styles.accessSubtitle}>Generate Access Code</h3>

                                {selectedTreatment ? (
                                    <div style={styles.selectedTreatmentDetails}>
                                        <h4 style={styles.detailsTitle}>Selected Treatment</h4>
                                        <div style={{ ...styles.detailsContent, textAlign: "center" }}>
                                            {/* 讓資訊置中，或至少不再緊貼左側 */}
                                            <p><strong>Date & Time:</strong> {formatDate(selectedTreatment.startTime)} at {formatTime(selectedTreatment.startTime)}</p>
                                            <p><strong>Guest:</strong> {selectedTreatment.guestFirstName} {selectedTreatment.guestLastName}</p>
                                            {selectedTreatment.clientId && (
                                                <p><strong>Customer ID:</strong> {selectedTreatment.clientId}</p>
                                            )}
                                            <p><strong>Treatment:</strong> {selectedTreatment.treatmentType} ({selectedTreatment.duration} min)</p>
                                            <p><strong>Therapist:</strong> {selectedTreatment.therapistName}</p>
                                            <p><strong>Room:</strong> {selectedTreatment.roomNumber}</p>
                                        </div>

                                        <div style={{ display: "flex", justifyContent: "center", margin: "28px 0 16px" }}>
                                            <button
                                                style={{
                                                    ...styles.generateCodeButton,
                                                    width: "220px", // 給個適中寬度
                                                    margin: 0,
                                                }}
                                                onClick={() => handleGenerateAccessCode(selectedTreatment)}
                                                disabled={!selectedTreatment.clientId || accessCodeLoading}
                                            >
                                                {accessCodeLoading ? 'Generating...' : 'Generate Access Code'}
                                            </button>
                                        </div>

                                        {!selectedTreatment.clientId && (
                                            <p style={styles.warningText1}>
                                                This treatment doesn't have a customer ID associated with it. Access code cannot be generated.
                                            </p>
                                        )}

                                        {accessCode && (
                                            <div style={{
                                                ...styles.accessCodeResult,
                                                margin: "32px auto 0 auto",
                                                maxWidth: "320px",
                                                textAlign: "center"
                                            }}>
                                                <h4 style={styles.codeTitle}>Access Code</h4>
                                                <div style={styles.codeDisplay}>
                                                    <span style={styles.codeValue}>{accessCode.code}</span>
                                                </div>
                                                <p style={styles.codeExpiry}>
                                                    This code will expire on {new Date(accessCode.expiryTime * 1000).toLocaleString()}
                                                </p>
                                                <div style={styles.codeInfo}>
                                                    <p style={styles.codeInfoText}>
                                                        <strong>For:</strong> {selectedTreatment.guestFirstName} {selectedTreatment.guestLastName}'s {selectedTreatment.treatmentType}
                                                    </p>
                                                    <p style={styles.codeInfoText}>
                                                        <strong>On:</strong> {formatDate(selectedTreatment.startTime)} at {formatTime(selectedTreatment.startTime)}
                                                    </p>
                                                </div>
                                                <div style={{ display: "flex", justifyContent: "center" }}>
                                                    <button style={styles.shareButton}>
                                                        <span style={styles.shareIcon}>📱</span> Share code with guest!
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div style={styles.noSelectionMessage}>
                                        <p>Please select a treatment from the list on the left to generate an access code.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === "customers" && (
                    <div style={styles.customersContainer}>
                        <h2 style={styles.sectionTitle}>Activated Customers</h2>

                        <div style={styles.filterSection}>
                            <div style={styles.filterRow}>
                                <div style={styles.filterGroup}>
                                    <label style={styles.filterLabel}>Status</label>
                                    <select
                                        style={styles.filterSelect}
                                        name="status"
                                        value={customerFilters.status}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="">All Status</option>
                                        <option value="ACTIVE">Active</option>
                                        <option value="INACTIVE">Inactive</option>
                                    </select>
                                </div>

                                <div style={styles.filterGroup}>
                                    <label style={styles.filterLabel}>Therapist</label>
                                    <select
                                        style={styles.filterSelect}
                                        name="therapistName"
                                        value={customerFilters.therapistName}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="">All Therapists</option>
                                        <option value="Rea Hosana">Rea Hosana</option>
                                        <option value="Michelle Abalos">Michelle Abalos</option>
                                        <option value="Grace Marasigan">Grace Marasigan</option>
                                        <option value="Edlyn Molina">Edlyn Molina</option>
                                        <option value="Mary Ann Quiazon">Mary Ann Quiazon</option>
                                        <option value="Bernalie Torres">Bernalie Torres</option>
                                        <option value="Daisy Pangilinan">Daisy Pangilinan</option>
                                        <option value="Loisa Gelito">Loisa Gelito</option>
                                        <option value="Mary Jane Pamintuan">Mary Jane Pamintuan</option>
                                    </select>
                                </div>

                                <div style={styles.filterGroup}>
                                    <label style={styles.filterLabel}>Treatment Type</label>
                                    <select
                                        style={styles.filterSelect}
                                        name="treatmentType"
                                        value={customerFilters.treatmentType}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="">All Treatments</option>
                                        <option value="Wellness for Two">Wellness for Two</option>
                                        <option value="Pure Radiance">Pure Radiance</option>
                                        <option value="Vitality Booster">Vitality Booster</option>
                                        <option value="Mekeni Signature Massage">Mekeni Signature Massage</option>
                                        <option value="Purovel Mountain Stone Massage">Purovel Mountain Stone Massage</option>
                                        <option value="Refreshing Relaxation Massage">Refreshing Relaxation Massage</option>
                                        <option value="Wellbeing Muscle Recovery Massage">Wellbeing Muscle Recovery Massage</option>
                                        <option value="East Asian Massage">East Asian Massage</option>
                                        <option value="Sole & Leg Experience">Sole & Leg Experience</option>
                                        <option value="Bespoke Massage">Bespoke Massage</option>
                                        <option value="Ultra Comprehensive Anti-Aging Facial">Ultra Comprehensive Anti-Aging Facial</option>
                                        <option value="Comforting & Soothing Facial">Comforting & Soothing Facial</option>
                                        <option value="New Skin for Men">New Skin for Men</option>
                                        <option value="Perfect Contour Scrub">Perfect Contour Scrub</option>
                                        <option value="HImalayan Salt & Coconut Oil Scrub">HImalayan Salt & Coconut Oil Scrub</option>
                                    </select>
                                </div>
                            </div>

                            <div style={styles.filterRow}>
                                <div style={styles.filterGroup}>
                                    <label style={styles.filterLabel}>Start Date</label>
                                    <input
                                        type="text" // 改為 text 類型以允許自定義格式
                                        placeholder="dd/mm/yyyy"
                                        style={styles.filterInput}
                                        name="startDate"
                                        value={customerFilters.startDate}
                                        onChange={handleFilterChange}
                                    />
                                </div>

                                <div style={styles.filterGroup}>
                                    <label style={styles.filterLabel}>End Date</label>
                                    <input
                                        type="text" // 改為 text 類型以允許自定義格式
                                        placeholder="dd/mm/yyyy"
                                        style={styles.filterInput}
                                        name="endDate"
                                        value={customerFilters.endDate}
                                        onChange={handleFilterChange}
                                    />
                                </div>

                                <div style={styles.filterButtons}>
                                    <button
                                        style={styles.applyFilterButton}
                                        onClick={applyFilters}
                                    >
                                        Apply Filters
                                    </button>
                                    <button
                                        style={styles.resetFilterButton}
                                        onClick={resetFilters}
                                    >
                                        Reset
                                    </button>
                                </div>
                            </div>

                            <div style={styles.searchContainer}>
                                <input
                                    type="text"
                                    placeholder="Search by name, email or phone..."
                                    style={styles.searchInput}
                                    value={customerSearch}
                                    onChange={(e) => setCustomerSearch(e.target.value)}
                                />
                            </div>
                        </div>

                        {customersLoading ? (
                            <div style={styles.loadingContainer}>
                                <div style={styles.loadingSpinner}></div>
                                <p>Loading activated customers...</p>
                            </div>
                        ) : activatedCustomers.length === 0 ? (
                            <div style={styles.emptyState}>
                                <p style={styles.emptyText}>No activated customers found.</p>
                            </div>
                        ) : (
                            <div style={styles.customersTableContainer}>
                                <table style={styles.customersTable}>
                                    <thead>
                                        <tr>
                                            <th style={styles.tableHeader}>Name</th>
                                            <th style={styles.tableHeader}>Contact Info</th>
                                            <th style={styles.tableHeader}>Activation Details</th>
                                            <th style={styles.tableHeader}>Treatment</th>
                                            <th style={styles.tableHeader}>Therapist</th>
                                            <th style={styles.tableHeader}>Status</th>
                                            <th style={styles.tableHeader}>Interactions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {activatedCustomers
                                            .filter(customer => {
                                                if (!customerSearch) return true;

                                                const searchLower = customerSearch.toLowerCase();
                                                return (
                                                    (customer.guestFirstName + ' ' + customer.guestLastName).toLowerCase().includes(searchLower) ||
                                                    (customer.guestEmail || '').toLowerCase().includes(searchLower) ||
                                                    (customer.guestPhone || '').toLowerCase().includes(searchLower)
                                                );
                                            })
                                            .map((customer, index) => (
                                                <tr key={index} style={styles.tableRow}>
                                                    <td style={styles.tableCell}>
                                                        <div style={styles.customerNameCell}>
                                                            <span style={styles.customerName}>
                                                                {customer.guestFirstName} {customer.guestLastName}
                                                            </span>
                                                            <span style={styles.customerIdBadge} title="Customer ID">
                                                                {customer.customerId}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td style={styles.tableCell}>
                                                        <div style={styles.contactInfoCell}>
                                                            {customer.guestEmail && (
                                                                <span style={styles.emailInfo}>
                                                                    <span style={styles.iconLabel}>📧</span> {customer.guestEmail}
                                                                </span>
                                                            )}
                                                            {customer.guestPhone && (
                                                                <span style={styles.phoneInfo}>
                                                                    <span style={styles.iconLabel}>📱</span> {customer.guestPhone}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td style={styles.tableCell}>
                                                        <div style={styles.activationCell}>
                                                            <span style={styles.activationDate}>
                                                                <span style={styles.iconLabel}>📅</span> {formatDate(customer.activatedAt)}
                                                            </span>
                                                            <span style={styles.activationTime}>
                                                                <span style={styles.iconLabel}>🕒</span> {formatTime(customer.activatedAt)}
                                                            </span>
                                                            <span style={styles.activationCode}>
                                                                <span style={styles.iconLabel}>🔑</span> Code: {customer.activationCode}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td style={styles.tableCell}>
                                                        <div style={styles.treatmentCell}>
                                                            <span style={styles.treatmentName}>
                                                                {customer.treatmentType}
                                                            </span>
                                                            <span style={styles.treatmentId}>
                                                                ID: {customer.treatmentId}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td style={styles.tableCell}>
                                                        {customer.therapistName}
                                                    </td>
                                                    <td style={styles.tableCell}>
                                                        <span style={{
                                                            ...styles.statusBadge,
                                                            backgroundColor: customer.status === 'ACTIVE' ? '#4caf50' : '#9e9e9e'
                                                        }}>
                                                            {customer.status}
                                                        </span>
                                                    </td>
                                                    <td style={styles.tableCell}>
                                                        <div style={styles.interactionsCell}>
                                                            <span style={styles.interactionCount}>
                                                                {customer.totalInteractions || 0}
                                                            </span>
                                                            <span style={styles.lastActiveInfo}>
                                                                Last active: {formatDate(customer.lastActiveAt)}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td style={styles.tableCell}>
                                                        <div style={styles.actionButtons}>

                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === "reports" && (
                    <div style={styles.reportsContainer}>
                        <h2 style={styles.sectionTitle}>Treatment Sleep Reports</h2>

                        <div style={styles.reportFilters}>
                            <div style={styles.filterRow}>
                                <div style={styles.filterGroup}>
                                    <label style={styles.filterLabel}>Select Treatment</label>
                                    <select
                                        style={styles.filterSelect}
                                        value={selectedTreatmentId || ''}
                                        onChange={(e) => handleTreatmentSelect(e.target.value)}
                                    >
                                        <option value="">-- Select a treatment --</option>
                                        {treatments.map(treatment => (
                                            <option
                                                key={treatment.treatmentId}
                                                value={treatment.treatmentId}
                                            >
                                                {treatment.guestFirstName} {treatment.guestLastName} - {treatment.treatmentType} - {formatDate(treatment.startTime)} {formatTime(treatment.startTime)}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                            </div>

                            {selectedTreatment && (
                                <div style={styles.selectedTreatmentInfo}>
                                    <h4 style={styles.selectedTreatmentTitle}>Selected Treatment Details</h4>
                                    <div style={styles.selectedTreatmentDetails}>
                                        <div style={styles.detailItem}>
                                            <span style={styles.detailLabel}>Guest:</span>
                                            <span style={styles.detailValue}>{selectedTreatment.guestFirstName} {selectedTreatment.guestLastName}</span>
                                        </div>
                                        <div style={styles.detailItem}>
                                            <span style={styles.detailLabel}>Room:</span>
                                            <span style={styles.detailValue}>{selectedTreatment.roomNumber}</span>
                                        </div>
                                        <div style={styles.detailItem}>
                                            <span style={styles.detailLabel}>Date:</span>
                                            <span style={styles.detailValue}>{formatDate(selectedTreatment.startTime)}</span>
                                        </div>
                                        <div style={styles.detailItem}>
                                            <span style={styles.detailLabel}>Time:</span>
                                            <span style={styles.detailValue}>
                                                {formatTime(selectedTreatment.startTime)} - {formatTime(selectedTreatment.endTime || new Date(new Date(selectedTreatment.startTime).getTime() + selectedTreatment.duration * 60000))}
                                            </span>
                                        </div>
                                        <div style={styles.detailItem}>
                                            <span style={styles.detailLabel}>Treatment:</span>
                                            <span style={styles.detailValue}>{selectedTreatment.treatmentType} ({selectedTreatment.duration} min)</span>
                                        </div>
                                        {deviceInfo && (
                                            <>
                                                <div style={styles.detailItem}>
                                                    <span style={styles.detailLabel}>Device SN:</span>
                                                    <span style={styles.detailValue}>{deviceInfo.serialNumber}</span>
                                                </div>
                                                <div style={styles.detailItem}>
                                                    <span style={styles.detailLabel}>MAC Address:</span>
                                                    <span style={styles.detailValue}>{deviceInfo.macAddress}</span>
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    <button
                                        style={deviceInfo ? styles.generateReportButton : styles.disabledButton}
                                        onClick={generateSleepReport}
                                        disabled={!deviceInfo || reportLoading}
                                    >
                                        {reportLoading ? 'Generating...' : 'Generate Sleep Report'}
                                    </button>

                                    {!deviceInfo && (
                                        <div style={styles.warningText1}>
                                            No device is mapped to this room. Sleep report cannot be generated.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {reportLoading ? (
                            <div style={styles.loadingContainer}>
                                <div style={styles.loadingSpinner}></div>
                                <p>Generating sleep report...</p>
                            </div>
                        ) : reportData ? (
                            <div style={styles.reportResultsContainer} className="reportResultsContainer">
                                <div style={styles.reportHeader}>
                                    <h3 style={styles.reportTitle}>
                                        Relaxation Report
                                        {selectedTreatment === sampleTreatment && <span style={styles.sampleTag}>SAMPLE</span>}
                                    </h3>
                                    <div style={styles.reportActions}>
                                        <button
                                            style={styles.reportExportButton}
                                            onClick={handleExportPDF}
                                            disabled={reportLoading}
                                        >
                                            <span style={styles.actionIcon}>📊</span>
                                            <span style={styles.actionText}>
                                                {isPdfExporting ? 'Generating PDF...' : 'Export Formal PDF'}
                                            </span>
                                        </button>
                                        {/* <button style={styles.reportShareButton} onClick={shareReport}>
        <span style={styles.actionIcon}>📤</span>
        <span style={styles.actionText}>Share</span>
    </button> */}
                                    </div>
                                </div>

                                <div style={styles.reportCards}>
                                    <div style={styles.reportCard}>
                                        <div style={styles.reportCardIcon}>⏱️</div>
                                        <div style={styles.reportCardContent}>
                                            <h4 style={styles.reportCardTitle}>Relaxation Duration</h4>
                                            <p style={styles.reportCardValue}>{reportData.totalSleep} min</p>
                                            <p style={styles.reportCardDescription}>
                                                Total relaxation time recorded
                                            </p>
                                        </div>
                                    </div>

                                    <div style={styles.reportCard}>
                                        <div style={styles.reportCardIcon}>❤️</div>
                                        <div style={styles.reportCardContent}>
                                            <h4 style={styles.reportCardTitle}>Average Heart Rate</h4>
                                            <p style={styles.reportCardValue}>{reportData.avgHr} BPM</p>
                                            <p style={styles.reportCardDescription}>
                                                Average heart rate during treatment
                                            </p>
                                        </div>
                                    </div>

                                    <div style={styles.reportCard}>
                                        <div style={styles.reportCardIcon}>🫁</div>
                                        <div style={styles.reportCardContent}>
                                            <h4 style={styles.reportCardTitle}>Average Respiration</h4>
                                            <p style={styles.reportCardValue}>{reportData.avgRr} breaths/min</p>
                                            <p style={styles.reportCardDescription}>
                                                Average breathing rate during treatment
                                            </p>
                                        </div>
                                    </div>

                                    <div style={styles.reportCard}>
                                        <div style={styles.reportCardIcon}>🏆</div>
                                        <div style={styles.reportCardContent}>
                                            <h4 style={styles.reportCardTitle}>Relaxation Score</h4>
                                            <p style={styles.reportCardValue}>{reportData.score}/100</p>
                                            <p style={styles.reportCardDescription}>
                                                Overall treatment effectiveness score
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div style={styles.visualRepresentation}>
                                    <h4 style={styles.visualTitle}>Relaxation Visual Summary</h4>

                                    <div style={styles.treatmentComparisonSection}>
                                        <h5 style={styles.comparisonTitle}>Treatment Effectiveness</h5>

                                        {reportData && reportData.rawData && (() => {
                                            // 計算治療初期和後期的數據
                                            const respiratoryData = reportData.rawData.respiratoryRate;
                                            const heartRateData = reportData.rawData.heartRate;

                                            if (!respiratoryData || !respiratoryData.values || !respiratoryData.values.length ||
                                                !heartRateData || !heartRateData.values || !heartRateData.values.length) {
                                                return null;
                                            }

                                            // 確定初期和後期的數據點數量 (使用前15%和後15%的數據點)
                                            const initialPeriodSize = Math.max(Math.floor(respiratoryData.values.length * 0.15), 1);
                                            const finalPeriodSize = Math.max(Math.floor(respiratoryData.values.length * 0.15), 1);

                                            // 提取初期數據
                                            const initialRR = respiratoryData.values.slice(0, initialPeriodSize);
                                            const initialHR = heartRateData.values.slice(0, initialPeriodSize);

                                            // 提取後期數據
                                            const finalRR = respiratoryData.values.slice(-finalPeriodSize);
                                            const finalHR = heartRateData.values.slice(-finalPeriodSize);

                                            // 計算平均值
                                            const avgInitialRR = initialRR.reduce((sum, val) => sum + val, 0) / initialRR.length;
                                            const avgInitialHR = initialHR.reduce((sum, val) => sum + val, 0) / initialHR.length;
                                            const avgFinalRR = finalRR.reduce((sum, val) => sum + val, 0) / finalRR.length;
                                            const avgFinalHR = finalHR.reduce((sum, val) => sum + val, 0) / finalHR.length;

                                            // 計算初期和後期的放鬆評分
                                            const initialScore = calculateRelaxationScore(
                                                { avg: avgInitialHR, min: Math.min(...initialHR), max: Math.max(...initialHR) },
                                                { avg: avgInitialRR, min: Math.min(...initialRR), max: Math.max(...initialRR) }
                                            );

                                            const finalScore = calculateRelaxationScore(
                                                { avg: avgFinalHR, min: Math.min(...finalHR), max: Math.max(...finalHR) },
                                                { avg: avgFinalRR, min: Math.min(...finalRR), max: Math.max(...finalRR) }
                                            );

                                            const improvement = finalScore - initialScore;
                                            const isImproved = improvement > 0;

                                            return (
                                                <div style={styles.comparisonContainer}>
                                                    {/* 桌面端左右佈局，移動端上下佈局 */}
                                                    <div className="comparison-flex-container" style={styles.comparisonFlexContainer}>
                                                        {/* 左側：初期評分 */}
                                                        <div style={styles.comparisonColumn}>
                                                            <div style={styles.phaseLabel}>Beginning</div>
                                                            <div style={styles.smallerScoreVisual}>
                                                                <div style={styles.smallerScoreCircleContainer}>
                                                                    <div style={styles.scoreCircleBackground}></div>
                                                                    <div
                                                                        style={{
                                                                            ...styles.scoreCircleFill,
                                                                            background: `conic-gradient(
                                                ${getScoreColor(initialScore)} ${initialScore * 3.6}deg, 
                                                #f0f0f0 ${initialScore * 3.6}deg
                                            )`
                                                                        }}
                                                                    ></div>
                                                                    <div style={styles.scoreCircleInner}>
                                                                        <span style={styles.smallerScoreValue}>{Math.round(initialScore)}</span>
                                                                        <span style={styles.smallerScoreLabel}>Initial<br />Score</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div style={styles.phaseMetrics}>
                                                                <div style={styles.metricRow}>
                                                                    <span style={styles.metricLabel}>HR:</span>
                                                                    <span style={styles.metricValue}>{Math.round(avgInitialHR)} BPM</span>
                                                                </div>
                                                                <div style={styles.metricRow}>
                                                                    <span style={styles.metricLabel}>RR:</span>
                                                                    <span style={styles.metricValue}>{Math.round(avgInitialRR)}/min</span>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* 中間：變化指示 */}
                                                        <div style={styles.changeIndicator}>
                                                            <span style={styles.arrowIcon}>→</span>
                                                            <span style={{
                                                                ...styles.changeValue,
                                                                color: isImproved ? '#4CAF50' : '#F44336'
                                                            }}>
                                                                {isImproved ? '+' : ''}{Math.round(improvement)}
                                                            </span>
                                                        </div>

                                                        {/* 右側：結束評分 */}
                                                        <div style={styles.comparisonColumn}>
                                                            <div style={styles.phaseLabel}>Ending</div>
                                                            <div style={styles.smallerScoreVisual}>
                                                                <div style={styles.smallerScoreCircleContainer}>
                                                                    <div style={styles.scoreCircleBackground}></div>
                                                                    <div
                                                                        style={{
                                                                            ...styles.scoreCircleFill,
                                                                            background: `conic-gradient(
                                                ${getScoreColor(finalScore)} ${finalScore * 3.6}deg, 
                                                #f0f0f0 ${finalScore * 3.6}deg
                                            )`
                                                                        }}
                                                                    ></div>
                                                                    <div style={styles.scoreCircleInner}>
                                                                        <span style={styles.smallerScoreValue}>{Math.round(finalScore)}</span>
                                                                        <span style={styles.smallerScoreLabel}>Final<br />Score</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div style={styles.phaseMetrics}>
                                                                <div style={styles.metricRow}>
                                                                    <span style={styles.metricLabel}>HR:</span>
                                                                    <span style={styles.metricValue}>{Math.round(avgFinalHR)} BPM</span>
                                                                </div>
                                                                <div style={styles.metricRow}>
                                                                    <span style={styles.metricLabel}>RR:</span>
                                                                    <span style={styles.metricValue}>{Math.round(avgFinalRR)}/min</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* 效果描述文字 */}
                                                    <div style={styles.effectivenessDescription}>
                                                        <p style={styles.effectivenessText}>
                                                            {improvement >= 15 ?
                                                                `Excellent improvement! The treatment significantly enhanced relaxation levels with a ${Math.round(improvement)} point increase.` :
                                                                improvement >= 5 ?
                                                                    `Good response to treatment with a ${Math.round(improvement)} point increase in relaxation.` :
                                                                    improvement > 0 ?
                                                                        `Mild improvement in relaxation levels during treatment.` :
                                                                        `This session maintained a consistent relaxation level throughout.`
                                                            }
                                                        </p>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>

                                    {/* 整體放鬆評分可視化 */}
                                    <div style={styles.overallScoreSection}>
                                        <div style={styles.centeredScoreVisual}>
                                            <div style={styles.scoreCircleContainer}>
                                                <div style={styles.scoreCircleBackground}></div>
                                                <div
                                                    style={{
                                                        ...styles.scoreCircleFill,
                                                        background: `conic-gradient(
                            ${getScoreColor(reportData.score)} ${reportData.score * 3.6}deg, 
                            #f0f0f0 ${reportData.score * 3.6}deg
                        )`
                                                    }}
                                                ></div>
                                                <div style={styles.scoreCircleInner}>
                                                    <span style={styles.scoreValue}>{reportData.score}</span>
                                                    <span style={styles.scoreLabel}>Overall<br />Score</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={styles.scoreDescription}>
                                            <p style={styles.scoreText}>
                                                {getScoreDescription(reportData.score)}
                                            </p>
                                        </div>
                                    </div>

                                    {/* 生理指標可視化 - 心率 */}
                                    <div style={styles.metricItem}>
                                        <div style={styles.metricLabel}>Heart Rate</div>
                                        <div style={styles.metricBarOuter}>
                                            <div style={styles.metricBarContainer}>
                                                <div style={styles.metricBarZones}>
                                                    <div style={{ ...styles.metricBarZone, backgroundColor: '#4CAF50' }}></div>
                                                    <div style={{ ...styles.metricBarZone, backgroundColor: '#8BC34A' }}></div>
                                                    <div style={{ ...styles.metricBarZone, backgroundColor: '#FFEB3B' }}></div>
                                                    <div style={{ ...styles.metricBarZone, backgroundColor: '#FF9800' }}></div>
                                                    <div style={{ ...styles.metricBarZone, backgroundColor: '#F44336' }}></div>
                                                </div>

                                                <div
                                                    style={{
                                                        ...styles.metricIndicator,
                                                        left: `${((reportData.avgHr - 40) / 80) * 100}%`
                                                    }}
                                                >
                                                    <div style={{
                                                        ...styles.metricIndicatorCircle,
                                                        backgroundColor: getHeartRateColor(reportData.avgHr)
                                                    }}>
                                                        <span style={styles.metricValue}>{reportData.avgHr}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div style={styles.metricScale}>
                                                <span>40</span>
                                                <span>60</span>
                                                <span>80</span>
                                                <span>100</span>
                                                <span>120</span>
                                            </div>

                                            <div style={styles.metricUnit}>BPM</div>
                                        </div>

                                        <div style={styles.metricDescription}>
                                            <p style={styles.descriptionText}>
                                                {getHeartRateDescription(reportData.avgHr)}
                                            </p>
                                        </div>
                                    </div>

                                    {/* 生理指標可視化 - 呼吸率 */}
                                    <div style={styles.metricItem}>
                                        <div style={styles.metricLabel}>Respiration Rate</div>
                                        <div style={styles.metricBarOuter}>
                                            <div style={styles.metricBarContainer}>
                                                <div style={styles.metricBarZones}>
                                                    <div style={{ ...styles.metricBarZone, backgroundColor: '#4CAF50' }}></div>
                                                    <div style={{ ...styles.metricBarZone, backgroundColor: '#8BC34A' }}></div>
                                                    <div style={{ ...styles.metricBarZone, backgroundColor: '#FFEB3B' }}></div>
                                                    <div style={{ ...styles.metricBarZone, backgroundColor: '#FF9800' }}></div>
                                                    <div style={{ ...styles.metricBarZone, backgroundColor: '#F44336' }}></div>
                                                </div>

                                                <div
                                                    style={{
                                                        ...styles.metricIndicator,
                                                        left: `${((reportData.avgRr - 8) / 16) * 100}%`
                                                    }}
                                                >
                                                    <div style={{
                                                        ...styles.metricIndicatorCircle,
                                                        backgroundColor: getRespirationColor(reportData.avgRr)
                                                    }}>
                                                        <span style={styles.metricValue}>{reportData.avgRr}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div style={styles.metricScale}>
                                                <span>8</span>
                                                <span>12</span>
                                                <span>16</span>
                                                <span>20</span>
                                                <span>24</span>
                                            </div>

                                            <div style={styles.metricUnit}>breaths/min</div>
                                        </div>

                                        <div style={styles.metricDescription}>
                                            <p style={styles.descriptionText}>
                                                {getRespirationDescription(reportData.avgRr)}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div style={styles.reportSummary}>
                                    <h4 style={styles.reportSummaryTitle}>Treatment Relaxation Analysis</h4>
                                    <p style={styles.reportSummaryText}>
                                        This relaxation report for {selectedTreatment.guestFirstName} {selectedTreatment.guestLastName}'s {selectedTreatment.treatmentType}
                                        session shows data collected during the {selectedTreatment.duration} minute treatment on {formatDate(selectedTreatment.startTime)}.
                                    </p>
                                    <p style={styles.reportSummaryText}>
                                        The guest achieved a relaxation score of <strong>{reportData.score}/100</strong>, indicating
                                        {reportData.score >= 90 ? ' excellent' :
                                            reportData.score >= 75 ? ' good' :
                                                reportData.score >= 60 ? ' average' : ' below average'}
                                        relaxation during the treatment. Heart rate averaged <strong>{reportData.avgHr} BPM</strong> and respiration
                                        rate was <strong>{reportData.avgRr} breaths per minute</strong>.
                                    </p>
                                    <p style={styles.reportSummaryText}>
                                        The total relaxation time detected was <strong>{reportData.totalSleep} minutes</strong>, which represents
                                        <strong> {Math.round((reportData.totalSleep / selectedTreatment.duration) * 100)}%</strong> of the scheduled treatment time.
                                    </p>

                                    <div style={styles.recommendationsSection}>
                                        <h5 style={styles.recommendationsTitle}>Recommendations</h5>
                                        <ul style={styles.recommendationsList}>
                                            <li style={styles.recommendationItem}>
                                                {getRecommendation(reportData, selectedTreatment)}
                                            </li>
                                            <li style={styles.recommendationItem}>
                                                {reportData.avgHr > 75 ?
                                                    "Consider enhancing the relaxation environment with calming music and aromatherapy to help reduce heart rate." :
                                                    "The guest responded well to the treatment environment, showing good heart rate reduction."}
                                            </li>
                                            <li style={styles.recommendationItem}>
                                                Track relaxation trends over multiple sessions to customize treatment approach for optimal results.
                                            </li>
                                        </ul>
                                    </div>
                                </div>

                                {selectedTreatment === sampleTreatment && (
                                    <div style={styles.sampleDisclaimer}>
                                        <div style={styles.sampleDisclaimerIcon}>ℹ️</div>
                                        <p style={styles.sampleDisclaimerText}>
                                            This is a sample report for demonstration purposes only. The data shown is not from a real treatment session.
                                        </p>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div style={styles.noReportContainer}>
                                <div style={styles.noReportIcon}>🛌</div>
                                <h3 style={styles.noReportTitle}>No Sleep Report Generated</h3>
                                <p style={styles.noReportDescription}>
                                    Select a treatment from the dropdown above to generate a sleep report.
                                </p>
                                <p style={styles.noReportTips}>
                                    Sleep reports provide valuable insights into guest relaxation quality, monitoring heart rate, respiratory patterns,
                                    and overall sleep quality during treatments.
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === "settings" && (
                    <div style={styles.settingsContent}>
                        <h2 style={styles.sectionTitle}>Settings</h2>

                        <div style={styles.settingsSection}>
                            <h3 style={styles.settingsSectionTitle}>Time and Date Settings</h3>

                            <div style={styles.settingsGroup}>
                                <label style={styles.settingsLabel}>Time Zone</label>
                                <select
                                    style={styles.settingsSelect}
                                    value={userSettings.timezone}
                                    onChange={(e) => handleSettingsChange('timezone', e.target.value)}
                                >
                                    <option value="Asia/Manila">Manila (UTC+8)</option>
                                    <option value="Asia/Singapore">Singapore (UTC+8)</option>
                                    <option value="Asia/Hong_Kong">Hong Kong (UTC+8)</option>
                                    <option value="Asia/Tokyo">Tokyo (UTC+9)</option>
                                    <option value="Asia/Bangkok">Bangkok (UTC+7)</option>
                                </select>
                                <p style={styles.settingsHelp}>Select your local time zone for correct time display</p>
                            </div>

                            <div style={styles.settingsGroup}>
                                <label style={styles.settingsLabel}>Time Format</label>
                                <div style={styles.settingsOptions}>
                                    <label style={styles.settingsOption}>
                                        <input
                                            type="radio"
                                            name="timeFormat"
                                            value="12h"
                                            checked={userSettings.displayFormat === "12h"}
                                            onChange={() => handleSettingsChange('displayFormat', '12h')}
                                        />
                                        <span>12-hour (1:30 PM)</span>
                                    </label>
                                    <label style={styles.settingsOption}>
                                        <input
                                            type="radio"
                                            name="timeFormat"
                                            value="24h"
                                            checked={userSettings.displayFormat === "24h"}
                                            onChange={() => handleSettingsChange('displayFormat', '24h')}
                                        />
                                        <span>24-hour (13:30)</span>
                                    </label>
                                </div>
                            </div>

                            <div style={styles.settingsGroup}>
                                <label style={styles.settingsLabel}>Date Format</label>
                                <div style={styles.settingsOptions}>
                                    <label style={styles.settingsOption}>
                                        <input
                                            type="radio"
                                            name="dateFormat"
                                            value="MM/DD/YYYY"
                                            checked={userSettings.dateFormat === "MM/DD/YYYY"}
                                            onChange={() => handleSettingsChange('dateFormat', 'MM/DD/YYYY')}
                                        />
                                        <span>MM/DD/YYYY</span>
                                    </label>
                                    <label style={styles.settingsOption}>
                                        <input
                                            type="radio"
                                            name="dateFormat"
                                            value="DD/MM/YYYY"
                                            checked={userSettings.dateFormat === "DD/MM/YYYY"}
                                            onChange={() => handleSettingsChange('dateFormat', 'DD/MM/YYYY')}
                                        />
                                        <span>DD/MM/YYYY</span>
                                    </label>
                                    <label style={styles.settingsOption}>
                                        <input
                                            type="radio"
                                            name="dateFormat"
                                            value="YYYY-MM-DD"
                                            checked={userSettings.dateFormat === "YYYY-MM-DD"}
                                            onChange={() => handleSettingsChange('dateFormat', 'YYYY-MM-DD')}
                                        />
                                        <span>YYYY-MM-DD</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div style={styles.settingsSaveContainer}>
                            <button style={styles.settingsSaveButton}>
                                Save Settings
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* 新增療程模態框 */}
            {showTreatmentModal && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modalContent}>
                        <div style={styles.modalHeader}>
                            <h2 style={styles.modalTitle}>New Treatment</h2>
                            <button
                                style={styles.closeButton}
                                onClick={() => setShowTreatmentModal(false)}
                            >
                                ×
                            </button>
                        </div>

                        <form style={styles.treatmentForm} onSubmit={handleCreateTreatment}>
                            <div style={styles.formSection}>
                                <h3 style={styles.formSectionTitle}>Therapist Information</h3>
                                <div style={styles.formGroup}>
                                    <label style={styles.formLabel}>Therapist Name</label>
                                    <select
                                        style={styles.formSelect}
                                        name="therapistName"
                                        value={newTreatment.therapistName}
                                        onChange={handleTreatmentInputChange}
                                        required
                                    >
                                        <option value="">Please Select</option>
                                        <option value="Rea Hosana">Rea Hosana</option>
                                        <option value="Michelle Abalos">Michelle Abalos</option>
                                        <option value="Grace Marasigan">Grace Marasigan</option>
                                        <option value="Edlyn Molina">Edlyn Molina</option>
                                        <option value="Mary Ann Quiazon">Mary Ann Quiazon</option>
                                        <option value="Bernalie Torres">Bernalie Torres</option>
                                        <option value="Daisy Pangilinan">Daisy Pangilinan</option>
                                        <option value="Loisa Gelito">Loisa Gelito</option>
                                        <option value="Mary Jane Pamintuan">Mary Jane Pamintuan</option>
                                    </select>
                                </div>
                            </div>

                            <div style={styles.formSection}>
                                <h3 style={styles.formSectionTitle}>Guest Information</h3>
                                <div style={styles.formRow}>
                                    <div style={styles.formGroup}>
                                        <label style={styles.formLabel}>First Name</label>
                                        <input
                                            type="text"
                                            style={styles.formInput}
                                            name="guestFirstName"
                                            value={newTreatment.guestFirstName}
                                            onChange={handleTreatmentInputChange}
                                            required
                                        />
                                    </div>
                                    <div style={styles.formGroup}>
                                        <label style={styles.formLabel}>Last Name</label>
                                        <input
                                            type="text"
                                            style={styles.formInput}
                                            name="guestLastName"
                                            value={newTreatment.guestLastName}
                                            onChange={handleTreatmentInputChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div style={styles.formGroup}>
                                    <label style={styles.formLabel}>Email</label>
                                    <input
                                        type="email"
                                        style={styles.formInput}
                                        placeholder="example@example.com"
                                        name="guestEmail"
                                        value={newTreatment.guestEmail}
                                        onChange={handleTreatmentInputChange}
                                    />
                                </div>

                                <div style={styles.formRow}>
                                    <div style={styles.formGroupSmall}>
                                        <label style={styles.formLabel}>Country Code</label>
                                        <input
                                            type="text"
                                            style={styles.formInput}
                                            placeholder="+63"
                                            name="countryCode"
                                            value={newTreatment.countryCode}
                                            onChange={handleTreatmentInputChange}
                                        />
                                    </div>
                                    <div style={styles.formGroupLarge}>
                                        <label style={styles.formLabel}>Phone Number</label>
                                        <input
                                            type="text"
                                            style={styles.formInput}
                                            placeholder="9123456789"
                                            name="phoneNumber"
                                            value={newTreatment.phoneNumber}
                                            onChange={handleTreatmentInputChange}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div style={styles.formSection}>
                                <h3 style={styles.formSectionTitle}>Treatment Details</h3>
                                <div style={styles.formGroup}>
                                    <label style={styles.formLabel}>Spa Room Number</label>
                                    <select
                                        style={styles.formSelect}
                                        name="spaRoomNumber"
                                        value={newTreatment.spaRoomNumber}
                                        onChange={handleTreatmentInputChange}
                                        required
                                    >
                                        <option value="">Please Select</option>
                                        <option value="Ischi">Ischi</option>
                                        <option value="Lavandra">Lavandra</option>
                                        <option value="Pign">Pign</option>
                                        <option value="Schember">Schember</option>
                                        <option value="Ruver (couple)">Ruver (couple)</option>
                                    </select>
                                </div>

                                <div style={styles.formRow}>
                                    <div style={styles.formGroup}>
                                        <label style={styles.formLabel}>Date</label>
                                        <input
                                            type="date"
                                            style={styles.formInput}
                                            name="date"
                                            value={newTreatment.date}
                                            onChange={handleTreatmentInputChange}
                                            required
                                        />
                                    </div>

                                    <div style={styles.formGroup}>
                                        <label style={styles.formLabel}>Start Time</label>
                                        <div style={styles.timeInputGroup}>
                                            <input
                                                type="number"
                                                min="1"
                                                max="12"
                                                style={styles.timeInput}
                                                name="startTimeHour"
                                                value={newTreatment.startTimeHour || ""}
                                                placeholder="HH"
                                                onChange={(e) => {
                                                    const value = Math.min(12, Math.max(1, parseInt(e.target.value) || ""));
                                                    setNewTreatment(prev => ({
                                                        ...prev,
                                                        startTimeHour: value.toString()
                                                    }));
                                                }}
                                                required
                                            />
                                            <span style={styles.timeSeparator}>:</span>
                                            <input
                                                type="number"
                                                min="0"
                                                max="59"
                                                step="5"
                                                style={styles.timeInput}
                                                name="startTimeMinute"
                                                value={newTreatment.startTimeMinute || ""}
                                                placeholder="MM"
                                                onChange={(e) => {
                                                    const value = Math.min(59, Math.max(0, parseInt(e.target.value) || 0));
                                                    const formattedValue = value.toString().padStart(2, '0');
                                                    setNewTreatment(prev => ({
                                                        ...prev,
                                                        startTimeMinute: formattedValue
                                                    }));
                                                }}
                                                required
                                            />
                                            <select
                                                style={styles.timeAmPm}
                                                name="startTimeAmPm"
                                                value={newTreatment.startTimeAmPm}
                                                onChange={handleTreatmentInputChange}
                                            >
                                                <option value="AM">AM</option>
                                                <option value="PM">PM</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div style={styles.formRow}>
                                    <div style={styles.formGroup}>
                                        <label style={styles.formLabel}>Is the customer pregnant?</label>
                                        <div style={styles.radioGroup}>
                                            <label style={styles.radioLabel}>
                                                <input
                                                    type="radio"
                                                    name="isPregnant"
                                                    value="true"
                                                    checked={newTreatment.isPregnant === true}
                                                    onChange={() => setNewTreatment(prev => ({ ...prev, isPregnant: true }))}
                                                /> Yes
                                            </label>
                                            <label style={styles.radioLabel}>
                                                <input
                                                    type="radio"
                                                    name="isPregnant"
                                                    value="false"
                                                    checked={newTreatment.isPregnant === false}
                                                    onChange={() => setNewTreatment(prev => ({ ...prev, isPregnant: false }))}
                                                /> No
                                            </label>
                                        </div>
                                    </div>

                                    <div style={styles.formGroup}>
                                        <label style={styles.formLabel}>Has respiratory/allergy concerns?</label>
                                        <div style={styles.radioGroup}>
                                            <label style={styles.radioLabel}>
                                                <input
                                                    type="radio"
                                                    name="hasRespiratoryConcerns"
                                                    value="true"
                                                    checked={newTreatment.hasRespiratoryConcerns === true}
                                                    onChange={() => setNewTreatment(prev => ({ ...prev, hasRespiratoryConcerns: true }))}
                                                /> Yes
                                            </label>
                                            <label style={styles.radioLabel}>
                                                <input
                                                    type="radio"
                                                    name="hasRespiratoryConcerns"
                                                    value="false"
                                                    checked={newTreatment.hasRespiratoryConcerns === false}
                                                    onChange={() => setNewTreatment(prev => ({ ...prev, hasRespiratoryConcerns: false }))}
                                                /> No
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div style={styles.formGroup}>
                                    <label style={styles.formLabel}>Treatment Type</label>
                                    <select
                                        style={styles.formSelect}
                                        name="treatmentType"
                                        value={newTreatment.treatmentType}
                                        onChange={handleTreatmentInputChange}
                                        required
                                    >
                                        <option value="">Please Select</option>
                                        <option value="Wellness for Two">Wellness for Two</option>
                                        <option value="Pure Radiance">Pure Radiance</option>
                                        <option value="Vitality Booster">Vitality Booster</option>
                                        <option value="Mekeni Signature Massage">Mekeni Signature Massage</option>
                                        <option value="Purovel Mountain Stone Massage">Purovel Mountain Stone Massage</option>
                                        <option value="Refreshing Relaxation Massage">Refreshing Relaxation Massage</option>
                                        <option value="Wellbeing Muscle Recovery Massage">Wellbeing Muscle Recovery Massage</option>
                                        <option value="East Asian Massage">East Asian Massage</option>
                                        <option value="Sole & Leg Experience">Sole & Leg Experience</option>
                                        <option value="Bespoke Massage">Bespoke Massage</option>
                                        <option value="Ultra Comprehensive Anti-Aging Facial">Ultra Comprehensive Anti-Aging Facial</option>
                                        <option value="Comforting & Soothing Facial">Comforting & Soothing Facial</option>
                                        <option value="New Skin for Men">New Skin for Men</option>
                                        <option value="Perfect Contour Scrub">Perfect Contour Scrub</option>
                                        <option value="HImalayan Salt & Coconut Oil Scrub">HImalayan Salt & Coconut Oil Scrub</option>
                                    </select>
                                </div>

                                <div style={styles.formGroup}>
                                    <label style={styles.formLabel}>Treatment Duration</label>
                                    <select
                                        style={styles.formSelect}
                                        name="treatmentDuration"
                                        value={newTreatment.treatmentDuration}
                                        onChange={handleTreatmentInputChange}
                                        required
                                    >
                                        <option value="">Please Select</option>
                                        <option value="45">45 min</option>
                                        <option value="50">50 min</option>
                                        <option value="60">60 min</option>
                                        <option value="75">75 min</option>
                                        <option value="90">90 min</option>
                                        <option value="120">120 min</option>
                                        <option value="150">150 min</option>
                                        <option value="180">180 min</option>
                                    </select>
                                </div>
                            </div>

                            <div style={styles.formActions}>
                                <button
                                    type="button"
                                    style={styles.cancelButton}
                                    onClick={() => setShowTreatmentModal(false)}
                                >
                                    Cancel
                                </button>
                                <button type="submit" style={styles.submitButton}>
                                    Create Treatment
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* 编辑治疗模态框 */}
            {editModalOpen && editingTreatment && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modalContent}>
                        <div style={styles.modalHeader}>
                            <h2 style={styles.modalTitle}>Edit Treatment</h2>
                            <button
                                style={styles.closeButton}
                                onClick={() => {
                                    setEditModalOpen(false);
                                    setEditingTreatment(null);
                                }}
                            >
                                ×
                            </button>
                        </div>

                        <form style={styles.treatmentForm} onSubmit={handleSubmitEdit}>
                            <div style={styles.formSection}>
                                <h3 style={styles.formSectionTitle}>Therapist Information</h3>
                                <div style={styles.formGroup}>
                                    <label style={styles.formLabel}>Therapist Name</label>
                                    <select
                                        style={styles.formSelect}
                                        name="therapistName"
                                        value={editingTreatment.therapistName}
                                        onChange={handleEditTreatmentInputChange}
                                        required
                                    >
                                        <option value="">Please Select</option>
                                        <option value="Rea Hosana">Rea Hosana</option>
                                        <option value="Michelle Abalos">Michelle Abalos</option>
                                        <option value="Grace Marasigan">Grace Marasigan</option>
                                        <option value="Edlyn Molina">Edlyn Molina</option>
                                        <option value="Mary Ann Quiazon">Mary Ann Quiazon</option>
                                        <option value="Bernalie Torres">Bernalie Torres</option>
                                        <option value="Daisy Pangilinan">Daisy Pangilinan</option>
                                        <option value="Loisa Gelito">Loisa Gelito</option>
                                        <option value="Mary Jane Pamintuan">Mary Jane Pamintuan</option>
                                    </select>
                                </div>
                            </div>

                            <div style={styles.formSection}>
                                <h3 style={styles.formSectionTitle}>Guest Information</h3>
                                <div style={styles.formRow}>
                                    <div style={styles.formGroup}>
                                        <label style={styles.formLabel}>First Name</label>
                                        <input
                                            type="text"
                                            style={styles.formInput}
                                            name="guestFirstName"
                                            value={editingTreatment.guestFirstName}
                                            onChange={handleEditTreatmentInputChange}
                                            required
                                        />
                                    </div>
                                    <div style={styles.formGroup}>
                                        <label style={styles.formLabel}>Last Name</label>
                                        <input
                                            type="text"
                                            style={styles.formInput}
                                            name="guestLastName"
                                            value={editingTreatment.guestLastName}
                                            onChange={handleEditTreatmentInputChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div style={styles.formGroup}>
                                    <label style={styles.formLabel}>Email</label>
                                    <input
                                        type="email"
                                        style={styles.formInput}
                                        placeholder="example@example.com"
                                        name="guestEmail"
                                        value={editingTreatment.guestEmail}
                                        onChange={handleEditTreatmentInputChange}
                                    />
                                </div>

                                <div style={styles.formRow}>
                                    <div style={styles.formGroupSmall}>
                                        <label style={styles.formLabel}>Country Code</label>
                                        <input
                                            type="text"
                                            style={styles.formInput}
                                            placeholder="+63"
                                            name="countryCode"
                                            value={editingTreatment.countryCode}
                                            onChange={handleEditTreatmentInputChange}
                                        />
                                    </div>
                                    <div style={styles.formGroupLarge}>
                                        <label style={styles.formLabel}>Phone Number</label>
                                        <input
                                            type="text"
                                            style={styles.formInput}
                                            placeholder="9123456789"
                                            name="phoneNumber"
                                            value={editingTreatment.phoneNumber}
                                            onChange={handleEditTreatmentInputChange}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div style={styles.formSection}>
                                <h3 style={styles.formSectionTitle}>Treatment Details</h3>
                                <div style={styles.formGroup}>
                                    <label style={styles.formLabel}>Spa Room Number</label>
                                    <select
                                        style={styles.formSelect}
                                        name="spaRoomNumber"
                                        value={editingTreatment.roomNumber}
                                        onChange={(e) => setEditingTreatment(prev => ({ ...prev, roomNumber: e.target.value }))}
                                        required
                                    >
                                        <option value="">Please Select</option>
                                        <option value="Ischi">Ischi</option>
                                        <option value="Lavandra">Lavandra</option>
                                        <option value="Pign">Pign</option>
                                        <option value="Schember">Schember</option>
                                        <option value="Ruver (couple)">Ruver (couple)</option>
                                    </select>
                                </div>

                                <div style={styles.formRow}>
                                    <div style={styles.formGroup}>
                                        <label style={styles.formLabel}>Date</label>
                                        <input
                                            type="date"
                                            style={styles.formInput}
                                            name="date"
                                            value={editingTreatment.date}
                                            onChange={handleEditTreatmentInputChange}
                                            required
                                        />
                                    </div>

                                    <div style={styles.formGroup}>
                                        <label style={styles.formLabel}>Start Time</label>
                                        <div style={styles.timeInputGroup}>
                                            <input
                                                type="number"
                                                min="1"
                                                max="12"
                                                style={styles.timeInput}
                                                name="startTimeHour"
                                                value={editingTreatment.startTimeHour || ""}
                                                placeholder="HH"
                                                onChange={(e) => {
                                                    const value = Math.min(12, Math.max(1, parseInt(e.target.value) || ""));
                                                    setEditingTreatment(prev => ({
                                                        ...prev,
                                                        startTimeHour: value.toString()
                                                    }));
                                                }}
                                                required
                                            />
                                            <span style={styles.timeSeparator}>:</span>
                                            <input
                                                type="number"
                                                min="0"
                                                max="59"
                                                step="5"
                                                style={styles.timeInput}
                                                name="startTimeMinute"
                                                value={editingTreatment.startTimeMinute || ""}
                                                placeholder="MM"
                                                onChange={(e) => {
                                                    const value = Math.min(59, Math.max(0, parseInt(e.target.value) || 0));
                                                    const formattedValue = value.toString().padStart(2, '0');
                                                    setEditingTreatment(prev => ({
                                                        ...prev,
                                                        startTimeMinute: formattedValue
                                                    }));
                                                }}
                                                required
                                            />
                                            <select
                                                style={styles.timeAmPm}
                                                name="startTimeAmPm"
                                                value={editingTreatment.startTimeAmPm}
                                                onChange={handleEditTreatmentInputChange}
                                            >
                                                <option value="AM">AM</option>
                                                <option value="PM">PM</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div style={styles.formRow}>
                                    <div style={styles.formGroup}>
                                        <label style={styles.formLabel}>Is the customer pregnant?</label>
                                        <div style={styles.radioGroup}>
                                            <label style={styles.radioLabel}>
                                                <input
                                                    type="radio"
                                                    name="isPregnant"
                                                    value="true"
                                                    checked={editingTreatment.isPregnant === true}
                                                    onChange={() => setEditingTreatment(prev => ({ ...prev, isPregnant: true }))}
                                                /> Yes
                                            </label>
                                            <label style={styles.radioLabel}>
                                                <input
                                                    type="radio"
                                                    name="isPregnant"
                                                    value="false"
                                                    checked={editingTreatment.isPregnant === false}
                                                    onChange={() => setEditingTreatment(prev => ({ ...prev, isPregnant: false }))}
                                                /> No
                                            </label>
                                        </div>
                                    </div>

                                    <div style={styles.formGroup}>
                                        <label style={styles.formLabel}>Has respiratory/allergy concerns?</label>
                                        <div style={styles.radioGroup}>
                                            <label style={styles.radioLabel}>
                                                <input
                                                    type="radio"
                                                    name="hasRespiratoryConcerns"
                                                    value="true"
                                                    checked={editingTreatment.hasRespiratoryConcerns === true}
                                                    onChange={() => setEditingTreatment(prev => ({ ...prev, hasRespiratoryConcerns: true }))}
                                                /> Yes
                                            </label>
                                            <label style={styles.radioLabel}>
                                                <input
                                                    type="radio"
                                                    name="hasRespiratoryConcerns"
                                                    value="false"
                                                    checked={editingTreatment.hasRespiratoryConcerns === false}
                                                    onChange={() => setEditingTreatment(prev => ({ ...prev, hasRespiratoryConcerns: false }))}
                                                /> No
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div style={styles.formGroup}>
                                    <label style={styles.formLabel}>Treatment Type</label>
                                    <select
                                        style={styles.formSelect}
                                        name="treatmentType"
                                        value={editingTreatment.treatmentType}
                                        onChange={handleEditTreatmentInputChange}
                                        required
                                    >
                                        <option value="">Please Select</option>
                                        <option value="Wellness for Two">Wellness for Two</option>
                                        <option value="Pure Radiance">Pure Radiance</option>
                                        <option value="Vitality Booster">Vitality Booster</option>
                                        <option value="Mekeni Signature Massage">Mekeni Signature Massage</option>
                                        <option value="Purovel Mountain Stone Massage">Purovel Mountain Stone Massage</option>
                                        <option value="Refreshing Relaxation Massage">Refreshing Relaxation Massage</option>
                                        <option value="Wellbeing Muscle Recovery Massage">Wellbeing Muscle Recovery Massage</option>
                                        <option value="East Asian Massage">East Asian Massage</option>
                                        <option value="Sole & Leg Experience">Sole & Leg Experience</option>
                                        <option value="Bespoke Massage">Bespoke Massage</option>
                                        <option value="Ultra Comprehensive Anti-Aging Facial">Ultra Comprehensive Anti-Aging Facial</option>
                                        <option value="Comforting & Soothing Facial">Comforting & Soothing Facial</option>
                                        <option value="New Skin for Men">New Skin for Men</option>
                                        <option value="Perfect Contour Scrub">Perfect Contour Scrub</option>
                                        <option value="HImalayan Salt & Coconut Oil Scrub">HImalayan Salt & Coconut Oil Scrub</option>
                                    </select>
                                </div>

                                <div style={styles.formGroup}>
                                    <label style={styles.formLabel}>Treatment Duration</label>
                                    <select
                                        style={styles.formSelect}
                                        name="treatmentDuration"
                                        value={editingTreatment.treatmentDuration}
                                        onChange={handleEditTreatmentInputChange}
                                        required
                                    >
                                        <option value="">Please Select</option>
                                        <option value="45">45 min</option>
                                        <option value="50">50 min</option>
                                        <option value="60">60 min</option>
                                        <option value="75">75 min</option>
                                        <option value="90">90 min</option>
                                        <option value="120">120 min</option>
                                        <option value="150">150 min</option>
                                        <option value="180">180 min</option>
                                    </select>
                                </div>

                                <div style={styles.formGroup}>
                                    <label style={styles.formLabel}>Status</label>
                                    <select
                                        style={styles.formSelect}
                                        name="status"
                                        value={editingTreatment.status}
                                        onChange={handleEditTreatmentInputChange}
                                        required
                                    >
                                        <option value="scheduled">Scheduled</option>
                                        <option value="in-progress">In Progress</option>
                                        <option value="completed">Completed</option>
                                        <option value="cancelled">Cancelled</option>
                                        {editingTreatment.status === 'ended' && <option value="ended">Ended</option>}
                                    </select>
                                </div>
                            </div>

                            <div style={styles.formActions}>
                                <button
                                    type="button"
                                    style={styles.cancelButton}
                                    onClick={() => {
                                        setEditModalOpen(false);
                                        setEditingTreatment(null);
                                    }}
                                >
                                    Cancel
                                </button>
                                <button type="submit" style={styles.submitButton}>
                                    Save Changes
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* 取消治療確認對話框 */}
            {cancelConfirmation.show && (
                <div style={styles.modalOverlay}>
                    <div style={styles.confirmDialog}>
                        <h3 style={styles.confirmTitle}>Cancel Treatment</h3>
                        <p style={styles.confirmMessage}>
                            You are about to cancel the {cancelConfirmation.treatment?.treatmentType} treatment for {cancelConfirmation.treatment?.guestFirstName} {cancelConfirmation.treatment?.guestLastName} on {formatDate(cancelConfirmation.treatment?.startTime)} at {formatTime(cancelConfirmation.treatment?.startTime)}.
                        </p>

                        <div style={styles.formGroup}>
                            <label style={styles.formLabel}>Cancellation Reason:</label>
                            <textarea
                                style={styles.formTextarea}
                                placeholder="Please provide a reason for cancellation"
                                value={cancelReason}
                                onChange={handleCancelReasonChange}
                                rows={3}
                            />
                        </div>

                        <div style={styles.confirmNote}>
                            Note: The treatment will be marked as cancelled but remains in your records for tracking purposes.
                        </div>

                        <div style={styles.confirmButtons}>
                            <button
                                onClick={handleCloseCancelDialog}
                                style={styles.cancelButton}
                            >
                                Back
                            </button>
                            <button
                                onClick={handleCancelTreatment}
                                style={styles.confirmCancelButton}
                            >
                                Cancel Treatment
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 永久刪除確認對話框 */}
            {deleteConfirmation.show && (
                <div style={styles.modalOverlay}>
                    <div style={styles.confirmDialog}>
                        <h3 style={styles.confirmTitle}>Permanent Deletion</h3>
                        <p style={styles.confirmMessage}>
                            You are about to <strong>permanently delete</strong> the {deleteConfirmation.treatment?.treatmentType} treatment for {deleteConfirmation.treatment?.guestFirstName} {deleteConfirmation.treatment?.guestLastName} on {formatDate(deleteConfirmation.treatment?.startTime)}.
                        </p>

                        <div style={styles.warningBox}>
                            <span style={styles.warningIcon}>⚠️</span>
                            <p style={styles.warningText}>
                                This action cannot be undone and all treatment records will be permanently removed from the system.
                            </p>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.formLabel}>Please enter "confirm":</label>
                            <input
                                type="password"
                                style={{
                                    ...styles.formInput,
                                    ...(deletePasswordError ? styles.inputError : {})
                                }}
                                placeholder="confirm"
                                value={deletePassword}
                                onChange={handleDeletePasswordChange}
                            />
                            {deletePasswordError && (
                                <p style={styles.errorMessage}>{deletePasswordError}</p>
                            )}
                        </div>

                        <div style={styles.confirmButtons}>
                            <button
                                onClick={handleCloseDeleteDialog}
                                style={styles.cancelButton}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteTreatment}
                                style={styles.confirmDeleteButton}
                                disabled={!deletePassword}
                            >
                                Delete Permanently
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 通知組件 */}
            {notification.show && (
                <div
                    style={{
                        ...styles.notification,
                        backgroundColor: notification.type === 'success' ? '#4caf50' : '#f44336'
                    }}
                >
                    <p style={styles.notificationMessage}>{notification.message}</p>
                    <button
                        onClick={hideNotification}
                        style={styles.notificationClose}
                    >
                        ✕
                    </button>
                </div>
            )}

            {/* Hidden PDF template with ID for PDF generation */}
{reportData && (
  <div 
    id="hidden-pdf-template" 
    style={{ position: 'absolute', left: '-9999px', top: 0, visibility: 'hidden' }}
  >
    <div style={{ width: '800px', padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <h1 style={{ textAlign: 'center', color: '#6d4c41' }}>Relaxation Report</h1>
      <h2 style={{ textAlign: 'center' }}>
        {selectedTreatment?.guestFirstName} {selectedTreatment?.guestLastName}
      </h2>
      <p style={{ textAlign: 'center' }}>
        {selectedTreatment?.treatmentType} • {formatDate(selectedTreatment?.startTime)}
      </p>
      
      <div style={{ marginTop: '30px' }}>
        <h3>Relaxation Score: {reportData.score}/100</h3>
        <p>{getScoreDescription(reportData.score)}</p>
        
        <h3>Heart Rate: {reportData.avgHr} BPM</h3>
        <p>{getHeartRateDescription(reportData.avgHr)}</p>
        
        <h3>Respiration Rate: {reportData.avgRr} breaths/min</h3>
        <p>{getRespirationDescription(reportData.avgRr)}</p>
        
        <h3>Relaxation Time: {reportData.totalSleep} minutes</h3>
      </div>
      
      <div style={{ marginTop: '30px', borderTop: '1px solid #ccc', paddingTop: '20px' }}>
        <p>Report generated on {new Date().toLocaleString()}</p>
        <p>Issued by: {selectedTreatment?.therapistName || 'Spa Therapist'}</p>
      </div>
    </div>
  </div>
)}
        </div>
    );
}


const keyframes = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes pulse {
    0% { box-shadow: 0 0 0 0 rgba(141, 110, 99, 0.4); }
    70% { box-shadow: 0 0 0 10px rgba(141, 110, 99, 0); }
    100% { box-shadow: 0 0 0 0 rgba(141, 110, 99, 0); }
  }
  
  @keyframes slideIn {
    from { transform: translateY(100px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;

const styles = {
    container: {
        display: "flex",
        minHeight: "100vh",
        fontFamily: "'Cormorant Garamond', 'Playfair Display', 'Baskerville', serif",
        backgroundColor: "#f9f5f1",
    },
    loadingContainer: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        width: "100%",
        backgroundColor: "#f9f5f1",
    },
    loadingSpinner: {
        width: "40px",
        height: "40px",
        border: "4px solid rgba(141, 110, 99, 0.2)",
        borderRadius: "50%",
        borderTopColor: "#8d6e63",
        animation: "spin 1s linear infinite",
        marginBottom: "20px",
    },
    loadingText: {
        color: "#8d6e63",
        fontSize: "16px",
        fontWeight: "500",
        letterSpacing: "0.5px",
    },
    errorContainer: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        width: "100%",
        backgroundColor: "#f9f5f1",
        padding: "20px",
        textAlign: "center",
    },
    errorTitle: {
        color: "#6d4c41",
        fontSize: "24px",
        marginBottom: "10px",
    },
    errorText: {
        color: "#a18072",
        fontSize: "16px",
        marginBottom: "30px",
    },
    errorButton: {
        padding: "10px 20px",
        backgroundColor: "#8d6e63",
        color: "white",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        fontWeight: "500",
        fontSize: "14px",
        transition: "all 0.2s",
    },
    emptyText: {
        color: "#a18072",
        fontSize: "16px",
        textAlign: "center",
        padding: "20px 0",
    },
    sidebar: {
        width: "250px",
        backgroundColor: "#f0e6d9",
        color: "#6d4c41",
        display: "flex",
        flexDirection: "column",
        boxShadow: "2px 0 10px rgba(0, 0, 0, 0.05)",
        zIndex: 10,
        padding: "20px 0",
        borderRight: "1px solid rgba(141, 110, 99, 0.1)",
    },
    logoContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 0 16px 0',
        background: '#f5f0e8',
        borderBottom: '1px solid #eee',
    },

    poweredByText: {
        fontSize: 16,
        color: '#888',
        marginBottom: 6,
    },

    logoImage: {
        height: 54,   // 調大這裡即可，原本 28，建議 48~60 之間
        marginBottom: 2,
        marginTop: 2,
        objectFit: 'contain',
        maxWidth: '160px'
    },

    logoSite: {
        fontSize: 12,
        color: '#333',
        marginTop: 2,
    },

    brandName: {
        margin: "5px 0 0 0",
        fontSize: "16px",
        fontWeight: "600",
        color: "#6d4c41",
        letterSpacing: "0.5px",
    },
    menuContainer: {
        flex: 1,
        padding: "20px 0",
    },
    menuItem: {
        display: "flex",
        alignItems: "center",
        padding: "12px 20px",
        cursor: "pointer",
        transition: "all 0.3s ease",
        borderLeft: "4px solid transparent",
    },
    activeMenuItem: {
        backgroundColor: "rgba(141, 110, 99, 0.1)",
        borderLeftColor: "#8d6e63",
    },
    menuIcon: {
        marginRight: "15px",
        fontSize: "18px",
    },
    menuText: {
        fontSize: "15px",
        fontWeight: "500",
        letterSpacing: "0.3px",
    },
    logoutContainer: {
        padding: "20px 0",
        borderTop: "1px solid rgba(141, 110, 99, 0.1)",
    },
    mainContent: {
        flex: 1,
        backgroundColor: "#faf8f6",
        padding: "30px",
        overflow: "auto",
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "30px",
        animation: "fadeIn 0.5s ease-out",
    },
    pageTitle: {
        flex: 1,
    },
    organizationName: {
        fontSize: "28px",
        fontWeight: "600",
        color: "#6d4c41",
        margin: "0 0 5px 0",
        letterSpacing: "0.5px",
    },
    welcomeText: {
        fontSize: "16px",
        color: "#a18072",
        margin: 0,
        fontWeight: "400",
    },
    userInfo: {
        display: "flex",
        alignItems: "center",
    },
    refreshButton: {
        display: "flex",
        alignItems: "center",
        padding: "8px 15px",
        marginRight: "15px",
        backgroundColor: "#f0e6d9",
        color: "#6d4c41",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        transition: "all 0.2s ease",
    },
    refreshIcon: {
        marginRight: "8px",
        fontSize: "16px",
    },
    refreshingSpin: {
        animation: "spin 1s linear infinite",
    },
    refreshText: {
        fontSize: "14px",
        fontWeight: "500",
    },
    notification: {
        position: "fixed",
        bottom: "24px",
        right: "24px",
        padding: "12px 20px",
        borderRadius: "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        zIndex: 1001,
        animation: "slideIn 0.3s ease-out"
    },
    notificationMessage: {
        margin: "0 24px 0 0",
        color: "white",
        fontSize: "14px"
    },
    notificationClose: {
        background: "none",
        border: "none",
        color: "white",
        cursor: "pointer",
        fontSize: "16px",
        padding: "4px"
    },
    notificationIcon: {
        fontSize: "20px",
        color: "#8d6e63",
    },
    notificationBadge: {
        position: "absolute",
        top: "-8px",
        right: "-8px",
        backgroundColor: "#c94b32",
        color: "white",
        fontSize: "11px",
        fontWeight: "bold",
        width: "18px",
        height: "18px",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    userProfile: {
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
    },
    userAvatar: {
        width: "40px",
        height: "40px",
        borderRadius: "50%",
        backgroundColor: "#8d6e63",
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: "bold",
        fontSize: "18px",
        marginRight: "10px",
    },
    userDetails: {
        textAlign: "left",
    },
    userName: {
        fontSize: "15px",
        fontWeight: "500",
        color: "#6d4c41",
        margin: "0 0 2px 0",
    },
    userRole: {
        fontSize: "13px",
        color: "#a18072",
        margin: 0,
    },
    dashboardContent: {
        animation: "fadeIn 0.6s ease-out",
    },
    treatmentsContent: {
        animation: "fadeIn 0.6s ease-out",
    },
    comingSoonContent: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "50vh",
        textAlign: "center",
        animation: "fadeIn 0.6s ease-out",
    },
    comingSoonTitle: {
        fontSize: "24px",
        fontWeight: "600",
        color: "#6d4c41",
        margin: "0 0 15px 0",
    },
    comingSoonText: {
        fontSize: "16px",
        color: "#a18072",
    },
    statCards: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: "20px",
        marginBottom: "30px",
    },
    statCard: {
        backgroundColor: "white",
        borderRadius: "8px",
        padding: "20px",
        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
        transition: "all 0.3s ease",
        border: "1px solid rgba(141, 110, 99, 0.1)",
    },
    statTitle: {
        fontSize: "15px",
        color: "#a18072",
        margin: "0 0 10px 0",
        fontWeight: "500",
    },
    statValue: {
        fontSize: "28px",
        fontWeight: "bold",
        color: "#6d4c41",
        margin: "0 0 5px 0",
    },
    statInfo: {
        fontSize: "13px",
        color: "#a18072",
        margin: 0,
    },
    treatmentsSection: {
        backgroundColor: "white",
        borderRadius: "8px",
        padding: "20px",
        marginBottom: "30px",
        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
        border: "1px solid rgba(141, 110, 99, 0.1)",
    },
    sectionHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "20px",
    },
    treatmentList: {
        display: "flex",
        flexDirection: "column",
    },
    treatmentHeader: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1.5fr 1fr 0.7fr 0.8fr",
        padding: "10px 0",
        borderBottom: "1px solid rgba(141, 110, 99, 0.1)",
        fontWeight: "500",
        color: "#6d4c41",
    },
    treatmentHeaderItem: {
        fontSize: "14px",
        padding: "0 10px",
    },
    treatmentItem: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1.5fr 1fr 0.7fr 0.8fr",
        padding: "15px 0",
        borderBottom: "1px solid rgba(141, 110, 99, 0.05)",
        alignItems: "center",
    },
    treatmentInfo: {
        display: "flex",
        flexDirection: "column",
        padding: "0 10px",
    },
    treatmentDate: {
        fontSize: "14px",
        fontWeight: "500",
        color: "#6d4c41",
        marginBottom: "4px",
    },
    treatmentTime: {
        fontSize: "13px",
        color: "#a18072",
    },
    treatmentGuest: {
        padding: "0 10px",
        fontSize: "14px",
        color: "#6d4c41",
    },
    treatmentType: {
        display: "flex",
        flexDirection: "column",
        padding: "0 10px",
    },
    treatmentDuration: {
        fontSize: "13px",
        color: "#a18072",
        marginTop: "4px",
    },
    therapistName: {
        padding: "0 10px",
        fontSize: "14px",
        color: "#6d4c41",
    },
    roomNumber: {
        padding: "0 10px",
        fontSize: "14px",
        color: "#6d4c41",
    },
    treatmentStatus: {
        padding: "4px 10px",
        borderRadius: "4px",
        fontSize: "12px",
        fontWeight: "500",
        textAlign: "center",
        textTransform: "capitalize",
        display: "inline-block",
        margin: "0 10px",
    },
    statusScheduled: {
        backgroundColor: "rgba(33, 150, 243, 0.1)",
        color: "#2196F3",
    },
    statusInProgress: {
        backgroundColor: "rgba(255, 152, 0, 0.1)",
        color: "#FF9800",
    },
    statusCompleted: {
        backgroundColor: "rgba(76, 175, 80, 0.1)",
        color: "#4CAF50",
    },
    statusCancelled: {
        backgroundColor: "rgba(244, 67, 54, 0.1)",
        color: "#F44336",
    },
    viewMoreContainer: {
        display: "flex",
        justifyContent: "center",
        marginTop: "20px",
    },
    viewMoreButton: {
        padding: "8px 20px",
        backgroundColor: "#f0e6d9",
        color: "#6d4c41",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        fontWeight: "500",
        fontSize: "14px",
        transition: "all 0.2s",
    },
    recentActivity: {
        backgroundColor: "white",
        borderRadius: "8px",
        padding: "20px",
        marginBottom: "30px",
        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
        border: "1px solid rgba(141, 110, 99, 0.1)",
    },
    sectionTitle: {
        fontSize: "18px",
        fontWeight: "600",
        color: "#6d4c41",
        margin: "0 0 20px 0",
        letterSpacing: "0.3px",
    },
    activityList: {
        display: "flex",
        flexDirection: "column",
    },
    activityItem: {
        display: "flex",
        padding: "15px 0",
        borderBottom: "1px solid rgba(141, 110, 99, 0.1)",
    },
    activityTime: {
        width: "100px",
        fontSize: "14px",
        color: "#a18072",
        fontWeight: "500",
    },
    activityDetails: {
        flex: 1,
    },
    activityAction: {
        fontSize: "15px",
        color: "#6d4c41",
        margin: "0 0 3px 0",
        fontWeight: "500",
    },
    activityInfo: {
        fontSize: "13px",
        color: "#a18072",
        margin: 0,
    },
    quickActions: {
        backgroundColor: "white",
        borderRadius: "8px",
        padding: "20px",
        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
        border: "1px solid rgba(141, 110, 99, 0.1)",
    },
    actionButtons: {
        display: "flex",
        gap: "15px",
        flexWrap: "wrap",
    },
    actionButton: {
        display: "flex",
        alignItems: "center",
        padding: "12px 20px",
        backgroundColor: "#f0e6d9",
        color: "#6d4c41",
        border: "none",
        borderRadius: "6px",
        cursor: "pointer",
        transition: "all 0.2s ease",
        fontFamily: "'Cormorant Garamond', 'Playfair Display', serif",
        fontWeight: "500",
        fontSize: "14px",
    },
    actionIcon: {
        marginRight: "10px",
        fontSize: "16px",
    },
    actionText: {
        letterSpacing: "0.3px",
    },
    emptyState: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "50px 0",
        textAlign: "center",
    },

    // 新增療程模態框樣式
    modalOverlay: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
        animation: "fadeIn 0.3s ease-out",
    },
    modalContent: {
        backgroundColor: "#fff",
        borderRadius: "8px",
        width: "90%",
        maxWidth: "800px",
        maxHeight: "90vh",
        overflow: "auto",
        boxShadow: "0 5px 15px rgba(0, 0, 0, 0.1)",
        position: "relative",
    },
    modalHeader: {
        padding: "20px",
        borderBottom: "1px solid rgba(141, 110, 99, 0.1)",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        position: "sticky",
        top: 0,
        backgroundColor: "#fff",
        zIndex: 10,
    },
    modalTitle: {
        margin: 0,
        fontSize: "22px",
        fontWeight: "600",
        color: "#6d4c41",
    },
    closeButton: {
        background: "none",
        border: "none",
        fontSize: "24px",
        color: "#a18072",
        cursor: "pointer",
        padding: "0",
        lineHeight: "1",
    },
    treatmentForm: {
        padding: "20px",
    },
    formSection: {
        marginBottom: "30px",
    },
    formSectionTitle: {
        fontSize: "18px",
        fontWeight: "500",
        color: "#6d4c41",
        margin: "0 0 15px 0",
        borderBottom: "1px solid rgba(141, 110, 99, 0.1)",
        paddingBottom: "10px",
    },
    formRow: {
        display: "flex",
        gap: "20px",
        marginBottom: "15px",
    },
    formGroup: {
        flex: 1,
        marginBottom: "15px",
    },
    formGroupSmall: {
        flex: 0.3,
        marginBottom: "15px",
    },
    formGroupLarge: {
        flex: 0.7,
        marginBottom: "15px",
    },
    formLabel: {
        display: "block",
        fontSize: "14px",
        color: "#6d4c41",
        marginBottom: "5px",
        fontWeight: "500",
    },
    formInput: {
        width: "100%",
        padding: "10px 12px",
        border: "1px solid rgba(141, 110, 99, 0.2)",
        borderRadius: "4px",
        fontSize: "14px",
        color: "#6d4c41",
        backgroundColor: "#f9f7f5",
        transition: "border 0.3s",
        boxSizing: "border-box",
    },
    formSelect: {
        width: "100%",
        padding: "10px 12px",
        border: "1px solid rgba(141, 110, 99, 0.2)",
        borderRadius: "4px",
        fontSize: "14px",
        color: "#6d4c41",
        backgroundColor: "#f9f7f5",
        transition: "border 0.3s",
        boxSizing: "border-box",
    },
    timeInputGroup: {
        display: "flex",
        alignItems: "center",
        gap: "5px",
    },
    timeInput: {
        width: "60px",
        padding: "10px 12px",
        border: "1px solid rgba(141, 110, 99, 0.2)",
        borderRadius: "4px",
        fontSize: "14px",
        color: "#6d4c41",
        backgroundColor: "#f9f7f5",
        transition: "border 0.3s",
        boxSizing: "border-box",
    },
    timeAmPm: {
        width: "80px",
        padding: "10px 12px",
        border: "1px solid rgba(141, 110, 99, 0.2)",
        borderRadius: "4px",
        fontSize: "14px",
        color: "#6d4c41",
        backgroundColor: "#f9f7f5",
        transition: "border 0.3s",
        boxSizing: "border-box",
        marginLeft: "5px",
    },
    radioGroup: {
        display: "flex",
        gap: "15px",
    },
    radioLabel: {
        display: "flex",
        alignItems: "center",
        fontSize: "14px",
        color: "#6d4c41",
        cursor: "pointer",
    },
    formActions: {
        display: "flex",
        justifyContent: "flex-end",
        gap: "15px",
        marginTop: "30px",
    },
    cancelButton: {
        padding: "10px 20px",
        backgroundColor: "#f1ece6",
        color: "#6d4c41",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        fontWeight: "500",
        fontSize: "14px",
        transition: "all 0.2s",
    },
    submitButton: {
        padding: "10px 20px",
        backgroundColor: "#8d6e63",
        color: "white",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        fontWeight: "500",
        fontSize: "14px",
        transition: "all 0.2s",
    },
    timeSeparator: {
        margin: "0 5px",
        alignSelf: "center",
        fontWeight: "bold",
        color: "#6d4c41",
    },
    customTimeInput: {
        display: "flex",
        alignItems: "center",
    },
    errorText: {
        color: "#c94b32",
        fontSize: "12px",
        marginTop: "5px",
    },
    // 設置頁面樣式
    settingsContent: {
        animation: "fadeIn 0.6s ease-out",
    },
    settingsSection: {
        backgroundColor: "white",
        borderRadius: "8px",
        padding: "20px",
        marginBottom: "20px",
        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
        border: "1px solid rgba(141, 110, 99, 0.1)",
    },
    settingsSectionTitle: {
        fontSize: "18px",
        fontWeight: "500",
        color: "#6d4c41",
        margin: "0 0 20px 0",
        borderBottom: "1px solid rgba(141, 110, 99, 0.1)",
        paddingBottom: "10px",
    },
    settingsGroup: {
        marginBottom: "25px",
    },
    settingsLabel: {
        display: "block",
        fontSize: "15px",
        fontWeight: "500",
        color: "#6d4c41",
        marginBottom: "10px",
    },
    settingsSelect: {
        width: "100%",
        maxWidth: "300px",
        padding: "10px 12px",
        border: "1px solid rgba(141, 110, 99, 0.2)",
        borderRadius: "4px",
        fontSize: "14px",
        color: "#6d4c41",
        backgroundColor: "#f9f7f5",
        marginBottom: "5px",
    },
    settingsHelp: {
        fontSize: "13px",
        color: "#a18072",
        margin: "5px 0 0 0",
    },
    settingsOptions: {
        display: "flex",
        flexDirection: "column",
        gap: "10px",
    },
    settingsOption: {
        display: "flex",
        alignItems: "center",
        fontSize: "14px",
        color: "#6d4c41",
        cursor: "pointer",
    },
    settingsSaveContainer: {
        display: "flex",
        justifyContent: "flex-end",
        marginTop: "20px",
    },
    settingsSaveButton: {
        padding: "10px 20px",
        backgroundColor: "#8d6e63",
        color: "white",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        fontWeight: "500",
        fontSize: "14px",
        transition: "all 0.2s",
    },
    // 表格样式
    treatmentsContainer: {
        backgroundColor: "white",
        borderRadius: "8px",
        padding: "20px",
        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
        animation: "fadeIn 0.6s ease-out",
    },
    tableContainer: {
        overflowX: "auto",
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
    },
    tableHeader: {
        textAlign: "left",
        padding: "12px 15px",
        borderBottom: "2px solid rgba(141, 110, 99, 0.2)",
        color: "#6d4c41",
        fontSize: "14px",
        fontWeight: "600",
    },
    tableRow: {
        borderBottom: "1px solid rgba(141, 110, 99, 0.1)",
    },
    tableCell: {
        padding: "12px 15px",
        color: "#6d4c41",
        fontSize: "14px",
    },
    statusBadge: {
        padding: "4px 10px",
        borderRadius: "4px",
        color: "white",
        fontSize: "12px",
        fontWeight: "500",
        display: "inline-block",
    },
    // 操作按鈕樣式
    actionButtons: {
        display: 'flex',
        gap: '8px',
        justifyContent: 'center'
    },
    editButton: {
        backgroundColor: '#8d6e63',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        width: '28px',
        height: '28px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        fontSize: '14px'
    },
    deleteButton: {
        backgroundColor: '#e57373',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        width: '28px',
        height: '28px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        fontSize: '14px'
    },
    cancelActionButton: {
        backgroundColor: '#ff9800', // 橙色
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        width: '28px',
        height: '28px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        fontSize: '14px'
    },
    quickCancelButton: {
        backgroundColor: '#ff9800', // 橙色
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        width: '28px',
        height: '28px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        fontSize: '14px'
    },
    restoreButton: {
        backgroundColor: '#4caf50', // 綠色
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        width: '28px',
        height: '28px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        fontSize: '14px'
    },

    // 確認對話框樣式
    confirmDialog: {
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px',
        width: '90%',
        maxWidth: '500px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        animation: 'fadeIn 0.3s ease-out'
    },
    confirmTitle: {
        margin: '0 0 16px 0',
        color: '#6d4c41',
        fontSize: '20px',
        fontWeight: '600'
    },
    confirmMessage: {
        margin: '0 0 16px 0',
        fontSize: '16px',
        lineHeight: '1.5',
        color: '#6d4c41'
    },
    confirmWarning: {
        margin: '0 0 24px 0',
        fontSize: '14px',
        color: '#e57373',
        fontWeight: '500'
    },
    confirmButtons: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px'
    },
    confirmCancelButton: {
        padding: '10px 16px',
        backgroundColor: '#ff9800', // 橙色
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500'
    },
    confirmDeleteButton: {
        padding: '10px 16px',
        backgroundColor: '#e57373',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500'
    },

    // 表單元素樣式
    formTextarea: {
        width: '100%',
        padding: '10px 12px',
        borderRadius: '4px',
        border: '1px solid #d7ccc8',
        fontSize: '14px',
        resize: 'vertical',
        minHeight: '60px',
        boxSizing: "border-box",
        color: "#6d4c41",
        backgroundColor: "#f9f7f5",
    },
    inputError: {
        borderColor: '#f44336',
    },
    errorMessage: {
        color: '#f44336',
        fontSize: '12px',
        marginTop: '4px',
        marginBottom: '0',
    },

    // 警告框樣式
    warningBox: {
        backgroundColor: '#ffebee',
        border: '1px solid #ffcdd2',
        borderRadius: '4px',
        padding: '12px',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'flex-start',
    },
    warningIcon: {
        fontSize: '18px',
        marginRight: '10px',
    },
    warningText: {
        margin: '0',
        fontSize: '14px',
        color: '#c62828',
        lineHeight: '1.5',
    },

    // 確認對話框註釋
    confirmNote: {
        fontSize: '14px',
        color: '#6d4c41',
        marginBottom: '20px',
        fontStyle: 'italic',
    },

    // 添加删除按钮样式
    removeButton: {
        backgroundColor: '#FFC107',  // 红色，表示删除操作
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        width: '28px',
        height: '28px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        fontSize: '14px'
    },
    // 訪問碼頁面樣式
    accessCodesContainer: {
        backgroundColor: "white",
        borderRadius: "8px",
        padding: "20px",
        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
        animation: "fadeIn 0.6s ease-out",
    },
    accessCodeIntro: {
        marginBottom: "20px",
        fontSize: "15px",
        color: "#6d4c41",
        lineHeight: "1.5",
    },
    accessCodeContentWrapper: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "20px",
    },
    treatmentSelectionPanel: {
        backgroundColor: "#f9f7f5",
        borderRadius: "6px",
        padding: "15px",
        border: "1px solid rgba(141, 110, 99, 0.1)",
    },
    accessCodePanel: {
        backgroundColor: "#f9f7f5",
        borderRadius: "6px",
        padding: "24px 18px",
        border: "1px solid rgba(141, 110, 99, 0.1)",
        minHeight: "340px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center", // 讓所有內容居中
        justifyContent: "flex-start",
    },
    accessSubtitle: {
        fontSize: "16px",
        fontWeight: "500",
        color: "#6d4c41",
        marginTop: "0",
        marginBottom: "15px",
        borderBottom: "1px solid rgba(141, 110, 99, 0.1)",
        paddingBottom: "10px",
    },
    treatmentListAccess: {
        maxHeight: "500px",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
    },
    accessTreatmentItem: {
        padding: "12px",
        backgroundColor: "white",
        borderRadius: "6px",
        border: "1px solid rgba(141, 110, 99, 0.1)",
        cursor: "pointer",
        transition: "all 0.2s ease",
    },
    selectedTreatmentItem: {
        border: "1px solid #8d6e63",
        boxShadow: "0 2px 8px rgba(141, 110, 99, 0.2)",
    },
    accessTreatmentHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "8px",
    },
    accessTreatmentDate: {
        fontSize: "14px",
        color: "#6d4c41",
        fontWeight: "500",
    },
    accessTreatmentTime: {
        fontSize: "14px",
        color: "#a18072",
    },
    accessTreatmentContent: {
        fontSize: "14px",
        color: "#6d4c41",
    },
    accessTreatmentGuest: {
        margin: "5px 0",
    },
    accessTreatmentType: {
        margin: "5px 0",
    },
    accessTreatmentDetails: {
        margin: "5px 0",
        display: "flex",
        justifyContent: "space-between",
    },
    selectedTreatmentDetails: {
        padding: "15px",
        backgroundColor: "white",
        borderRadius: "6px",
        border: "1px solid rgba(141, 110, 99, 0.1)",
    },
    detailsTitle: {
        fontSize: "16px",
        fontWeight: "500",
        color: "#6d4c41",
        margin: "0 0 15px 0",
        borderBottom: "1px solid rgba(141, 110, 99, 0.1)",
        paddingBottom: "10px",
        textAlign: "center"
    },
    detailsContent: {
        fontSize: "14px",
        color: "#6d4c41",
        marginBottom: "20px",
    },
    generateCodeButton: {
        width: "100%",
        maxWidth: "320px", // 或你想要的固定寬度
        padding: "12px",
        backgroundColor: "#8d6e63",
        color: "white",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        fontWeight: "500",
        fontSize: "14px",
        transition: "all 0.2s ease",
        margin: "24px auto 16px auto", // 這裡加
        display: "block",              // 這裡加
    },
    noSelectionMessage: {
        padding: "30px 15px",
        textAlign: "center",
        backgroundColor: "white",
        borderRadius: "6px",
        border: "1px solid rgba(141, 110, 99, 0.1)",
        color: "#a18072",
        fontSize: "15px",
    },
    accessCodeResult: {
        marginTop: "20px",
        padding: "15px",
        backgroundColor: "#f0e6d9",
        borderRadius: "6px",
        border: "1px solid rgba(141, 110, 99, 0.2)",
    },
    codeTitle: {
        fontSize: "16px",
        fontWeight: "500",
        color: "#6d4c41",
        margin: "0 0 15px 0",
    },
    codeDisplay: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "20px",
        backgroundColor: "white",
        borderRadius: "6px",
        marginBottom: "15px",
    },
    codeValue: {
        fontSize: "32px",
        fontWeight: "700",
        color: "#6d4c41",
        letterSpacing: "3px",
    },
    codeExpiry: {
        fontSize: "13px",
        color: "#a18072",
        marginBottom: "15px",
        textAlign: "center",
        fontStyle: "italic",
    },
    shareOptions: {
        display: "flex",
        gap: "10px",
    },
    shareButton: {
        flex: 1,
        padding: "10px",
        backgroundColor: "white",
        color: "#6d4c41",
        border: "1px solid rgba(141, 110, 99, 0.2)",
        borderRadius: "4px",
        cursor: "pointer",
        fontWeight: "500",
        fontSize: "13px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    shareIcon: {
        marginRight: "8px",
        fontSize: "16px",
    },
    // 添加到樣式對象
    clientIdBadge: {
        marginLeft: "8px",
        fontSize: "12px",
        color: "#4caf50",
    },
    disabledButton: {
        width: "100%",
        padding: "12px",
        backgroundColor: "#cccccc",
        color: "#666666",
        border: "none",
        borderRadius: "4px",
        fontWeight: "500",
        fontSize: "14px",
        marginBottom: "20px",
        cursor: "not-allowed",
    },
    warningText1: {
        color: "#f44336",
        fontSize: "14px",
        margin: "0 0 20px 0",
        padding: "10px",
        backgroundColor: "rgba(244, 67, 54, 0.1)",
        borderRadius: "4px",
        textAlign: "center",
    },
    codeInfo: {
        padding: "10px",
        backgroundColor: "rgba(141, 110, 99, 0.05)",
        borderRadius: "4px",
        marginBottom: "15px",
    },
    codeInfoText: {
        margin: "5px 0",
        fontSize: "14px",
        color: "#6d4c41",
    },
    // 添加到樣式對象
    customersContainer: {
        backgroundColor: "white",
        borderRadius: "8px",
        padding: "20px",
        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
        animation: "fadeIn 0.6s ease-out",
    },
    filterSection: {
        marginBottom: "25px",
        backgroundColor: "#f9f7f5",
        borderRadius: "8px",
        padding: "20px",
        border: "1px solid rgba(141, 110, 99, 0.1)",
    },
    filterRow: {
        display: "flex",
        gap: "15px",
        marginBottom: "15px",
        alignItems: "flex-end",
    },
    filterGroup: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
    },
    filterLabel: {
        display: "block",
        fontSize: "14px",
        color: "#6d4c41",
        marginBottom: "8px",
        fontWeight: "500",
    },
    filterSelect: {
        width: "100%",
        padding: "10px 12px",
        borderRadius: "4px",
        border: "1px solid rgba(141, 110, 99, 0.2)",
        fontSize: "14px",
        color: "#6d4c41",
        backgroundColor: "white",
        appearance: "none", // 移除瀏覽器原生下拉箭頭
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236d4c41' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right 12px center",
        backgroundSize: "12px",
        paddingRight: "32px", // 為下拉箭頭騰出空間
    },
    filterInput: {
        width: "90%",
        padding: "10px 12px",
        borderRadius: "4px",
        border: "1px solid rgba(141, 110, 99, 0.2)",
        fontSize: "14px",
        color: "#6d4c41",
        backgroundColor: "white",
    },
    filterButtons: {
        display: "flex",
        gap: "10px",
        alignItems: "flex-end",
        marginTop: "28px", // 增加頂部距離以對齊其他輸入
    },
    applyFilterButton: {
        padding: "10px 16px",
        backgroundColor: "#8d6e63",
        color: "white",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        fontSize: "14px",
        fontWeight: "500",
    },
    resetFilterButton: {
        padding: "10px 16px",
        backgroundColor: "#f0e6d9",
        color: "#6d4c41",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        fontSize: "14px",
        fontWeight: "500",
    },
    searchContainer: {
        marginTop: "15px",
        padding: "0", // 確保沒有額外填充
    },
    searchInput: {
        width: "100%",
        padding: "10px 12px",
        borderRadius: "4px",
        border: "1px solid rgba(141, 110, 99, 0.2)",
        fontSize: "14px",
        color: "#6d4c41",
        backgroundColor: "white",
        boxShadow: "none", // 移除陰影效果
        outline: "none", // 移除點擊時的外框
        boxSizing: "border-box", // 確保寬度計算包括邊框
    },
    customersTableContainer: {
        overflowX: "auto",
    },
    customersTable: {
        width: "100%",
        borderCollapse: "collapse",
    },
    customerNameCell: {
        display: "flex",
        flexDirection: "column",
    },
    customerName: {
        fontWeight: "500",
        color: "#6d4c41",
    },
    customerIdBadge: {
        fontSize: "12px",
        color: "#a18072",
        marginTop: "4px",
    },
    contactInfoCell: {
        display: "flex",
        flexDirection: "column",
    },
    emailInfo: {
        marginBottom: "4px",
        fontSize: "13px",
    },
    phoneInfo: {
        fontSize: "13px",
    },
    iconLabel: {
        marginRight: "4px",
        fontSize: "12px",
    },
    activationCell: {
        display: "flex",
        flexDirection: "column",
    },
    activationDate: {
        fontWeight: "500",
        color: "#6d4c41",
        marginBottom: "2px",
    },
    activationTime: {
        fontSize: "13px",
        color: "#a18072",
        marginBottom: "4px",
    },
    activationCode: {
        fontSize: "12px",
        backgroundColor: "rgba(141, 110, 99, 0.1)",
        padding: "2px 6px",
        borderRadius: "4px",
        display: "inline-block",
    },
    treatmentCell: {
        display: "flex",
        flexDirection: "column",
    },
    treatmentName: {
        fontWeight: "500",
        color: "#6d4c41",
        marginBottom: "4px",
    },
    treatmentId: {
        fontSize: "12px",
        color: "#a18072",
    },
    interactionsCell: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    },
    interactionCount: {
        fontWeight: "bold",
        fontSize: "18px",
        color: "#8d6e63",
        marginBottom: "4px",
    },
    lastActiveInfo: {
        fontSize: "12px",
        color: "#a18072",
    },
    viewButton: {
        backgroundColor: "#8d6e63",
        color: "white",
        border: "none",
        borderRadius: "4px",
        width: "28px",
        height: "28px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        fontSize: "14px",
    },
    exportButton: {
        backgroundColor: "#4caf50",
        color: "white",
        border: "none",
        borderRadius: "4px",
        width: "28px",
        height: "28px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        fontSize: "14px",
    },
    // Additional styles for the Reports feature
    reportsContainer: {
        backgroundColor: "white",
        borderRadius: "8px",
        padding: "20px",
        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
        animation: "fadeIn 0.6s ease-out",
    },
    reportFilters: {
        backgroundColor: "#f9f7f5",
        borderRadius: "8px",
        padding: "20px",
        marginBottom: "25px",
        border: "1px solid rgba(141, 110, 99, 0.1)",
    },
    selectedTreatmentInfo: {
        backgroundColor: "white",
        borderRadius: "8px",
        padding: "15px",
        marginTop: "20px",
        border: "1px solid rgba(141, 110, 99, 0.1)",
    },
    selectedTreatmentTitle: {
        fontSize: "16px",
        fontWeight: "600",
        color: "#6d4c41",
        margin: "0 0 15px 0",
        borderBottom: "1px solid rgba(141, 110, 99, 0.1)",
        paddingBottom: "10px",
    },
    selectedTreatmentDetails: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
        gap: "10px",
        marginBottom: "20px",
    },
    detailItem: {
        display: "flex",
        flexDirection: "column",
        gap: "4px",
    },
    detailLabel: {
        fontSize: "13px",
        color: "#a18072",
        fontWeight: "500",
    },
    detailValue: {
        fontSize: "15px",
        color: "#6d4c41",
    },
    generateReportButton: {
        width: "100%",
        padding: "12px",
        backgroundColor: "#8d6e63",
        color: "white",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        fontWeight: "500",
        fontSize: "14px",
        transition: "all 0.2s ease",
        marginTop: "10px",
    },
    reportResultsContainer: {
        backgroundColor: "#f9f7f5",
        borderRadius: "8px",
        padding: "20px",
        border: "1px solid rgba(141, 110, 99, 0.1)",
    },
    reportHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "20px",
        borderBottom: "1px solid rgba(141, 110, 99, 0.1)",
        paddingBottom: "15px",
    },
    reportTitle: {
        margin: 0,
        fontSize: "20px",
        fontWeight: "600",
        color: "#6d4c41",
    },
    reportActions: {
        display: "flex",
        gap: "10px",
    },
    reportExportButton: {
        display: "flex",
        alignItems: "center",
        padding: "8px 15px",
        backgroundColor: "#8d6e63",
        color: "white",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        fontWeight: "500",
        fontSize: "14px",
    },
    reportShareButton: {
        display: "flex",
        alignItems: "center",
        padding: "8px 15px",
        backgroundColor: "#f0e6d9",
        color: "#6d4c41",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        fontWeight: "500",
        fontSize: "14px",
    },
    reportCards: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: "20px",
        marginBottom: "30px",
    },
    reportCard: {
        backgroundColor: "white",
        borderRadius: "8px",
        padding: "20px",
        boxShadow: "0 2px 5px rgba(0, 0, 0, 0.05)",
        display: "flex",
        alignItems: "center",
        border: "1px solid rgba(141, 110, 99, 0.1)",
    },
    reportCardIcon: {
        fontSize: "28px",
        marginRight: "15px",
        color: "#8d6e63",
    },
    reportCardContent: {
        flex: 1,
    },
    reportCardTitle: {
        margin: "0 0 5px 0",
        fontSize: "14px",
        fontWeight: "500",
        color: "#a18072",
    },
    reportCardValue: {
        margin: "0 0 5px 0",
        fontSize: "24px",
        fontWeight: "bold",
        color: "#6d4c41",
    },
    reportCardDescription: {
        margin: 0,
        fontSize: "12px",
        color: "#a18072",
    },
    reportCharts: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
        gap: "20px",
        marginBottom: "30px",
    },
    chartContainer: {
        backgroundColor: "white",
        borderRadius: "8px",
        padding: "15px",
        border: "1px solid rgba(141, 110, 99, 0.1)",
    },
    chartTitle: {
        fontSize: "16px",
        fontWeight: "500",
        color: "#6d4c41",
        margin: "0 0 15px 0",
        borderBottom: "1px solid rgba(141, 110, 99, 0.1)",
        paddingBottom: "10px",
    },
    chart: {
        height: "200px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    chartPlaceholder: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
    },
    chartIcon: {
        fontSize: "32px",
        color: "#d7ccc8",
        marginBottom: "10px",
    },
    chartText: {
        fontSize: "14px",
        color: "#a18072",
        margin: 0,
    },
    reportSummary: {
        backgroundColor: "white",
        borderRadius: "8px",
        padding: "20px",
        border: "1px solid rgba(141, 110, 99, 0.1)",
    },
    reportSummaryTitle: {
        fontSize: "16px",
        fontWeight: "600",
        color: "#6d4c41",
        margin: "0 0 15px 0",
        borderBottom: "1px solid rgba(141, 110, 99, 0.1)",
        paddingBottom: "10px",
    },
    reportSummaryText: {
        fontSize: "14px",
        color: "#6d4c41",
        lineHeight: "1.6",
        margin: "0 0 15px 0",
    },
    noReportContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "50px 20px",
        textAlign: "center",
    },
    noReportIcon: {
        fontSize: "48px",
        color: "#d7ccc8",
        marginBottom: "20px",
    },
    noReportTitle: {
        fontSize: "20px",
        fontWeight: "600",
        color: "#6d4c41",
        margin: "0 0 10px 0",
    },
    noReportDescription: {
        fontSize: "16px",
        color: "#a18072",
        margin: "0 0 15px 0",
        maxWidth: "500px",
    },
    noReportTips: {
        fontSize: "14px",
        color: "#a18072",
        margin: 0,
        maxWidth: "500px",
        fontStyle: "italic",
    },


    // 添加到styles对象
    visualRepresentation: {
        backgroundColor: "white",
        borderRadius: "8px",
        padding: "20px 15px",
        marginBottom: "30px",
        border: "1px solid rgba(141, 110, 99, 0.1)",
        boxSizing: "border-box",
        width: "100%",
        overflowX: "hidden",
        fontFamily: "'Cormorant Garamond', 'Playfair Display', 'Baskerville', serif",
    },
    visualTitle: {
        fontSize: "18px",
        fontWeight: "600",
        color: "#6d4c41",
        margin: "0 0 25px 0",
        textAlign: "center",
        borderBottom: "1px solid rgba(141, 110, 99, 0.1)",
        paddingBottom: "10px",
    },
    treatmentComparisonSection: {
        marginBottom: "30px",
        textAlign: "center",
    },
    comparisonTitle: {
        fontSize: "16px",
        fontWeight: "600",
        color: "#6d4c41",
        margin: "0 0 20px 0",
        textAlign: "center",
    },
    comparisonContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        maxWidth: "700px",
        margin: "0 auto",
    },
    comparisonFlexContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        // 桌面版水平排列
        flexDirection: "row",
    },
    comparisonColumn: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        flex: "1",
        maxWidth: "250px",
        padding: "0 15px",
    },
    phaseLabel: {
        fontSize: "16px",
        fontWeight: "500",
        color: "#6d4c41",
        margin: "10px 0",
        textAlign: "center",
    },
    // 较小的评分圆图（用于对比）
    smallerScoreVisual: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        margin: "10px 0",
    },
    smallerScoreCircleContainer: {
        position: "relative",
        width: "120px",
        height: "120px",
    },
    smallerScoreValue: {
        fontSize: "28px",
        fontWeight: "bold",
        color: "#6d4c41",
        lineHeight: "1",
    },
    smallerScoreLabel: {
        fontSize: "11px",
        color: "#a18072",
        textAlign: "center",
        marginTop: "3px",
    },
    // 整体评分圆图（比对比圆图大）
    centeredScoreVisual: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        margin: "10px 0",
    },
    scoreCircleContainer: {
        position: "relative",
        width: "150px",
        height: "150px",
    },
    scoreCircleBackground: {
        position: "absolute",
        width: "100%",
        height: "100%",
        borderRadius: "50%",
        background: "#f0f0f0",
    },
    scoreCircleFill: {
        position: "absolute",
        width: "100%",
        height: "100%",
        borderRadius: "50%",
        transform: "rotate(-90deg)",
    },
    scoreCircleInner: {
        position: "absolute",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: "70%",
        height: "70%",
        borderRadius: "50%",
        backgroundColor: "white",
        boxShadow: "0 0 10px rgba(0,0,0,0.05)",
    },
    scoreValue: {
        fontSize: "32px",
        fontWeight: "bold",
        color: "#6d4c41",
        lineHeight: "1",
    },
    scoreLabel: {
        fontSize: "12px",
        color: "#a18072",
        textAlign: "center",
        marginTop: "5px",
    },
    changeIndicator: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 10px",
        margin: "20px 0",
    },
    arrowIcon: {
        fontSize: "24px",
        color: "#8d6e63",
        marginBottom: "5px",
    },
    changeValue: {
        fontSize: "16px",
        fontWeight: "bold",
    },
    phaseMetrics: {
        margin: "5px 0 15px 0",
        width: "100%",
        maxWidth: "180px",
        textAlign: "center",
    },
    metricRow: {
        display: "flex",
        justifyContent: "space-between",
        fontSize: "14px",
        color: "#6d4c41",
        margin: "5px 0",
    },
    metricLabel: {
        fontWeight: "500",
    },
    metricValue: {
        color: "#6d4c41",
    },
    effectivenessDescription: {
        marginTop: "10px",
        marginBottom: "20px",
        padding: "10px 15px",
        backgroundColor: "rgba(141, 110, 99, 0.05)",
        borderRadius: "6px",
        width: "100%",
        maxWidth: "600px",
    },
    effectivenessText: {
        margin: "0",
        fontSize: "14px",
        color: "#6d4c41",
        textAlign: "center",
        lineHeight: "1.5",
    },
    overallScoreSection: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        marginBottom: "30px",
    },
    scoreDescription: {
        textAlign: "center",
        maxWidth: "500px",
        margin: "15px auto 0",
    },
    scoreText: {
        margin: "0",
        fontSize: "14px",
        color: "#6d4c41",
        lineHeight: "1.5",
    },
    // 保留原有的度量尺样式
    metricItem: {
        marginBottom: "30px",
        width: "100%",
    },
    metricBarOuter: {
        position: "relative",
        paddingBottom: "30px",
    },
    metricBarContainer: {
        position: "relative",
        height: "30px",
        backgroundColor: "transparent",
        borderRadius: "15px",
        overflow: "visible",
        marginBottom: "5px",
    },
    metricBarZones: {
        display: "flex",
        position: "absolute",
        width: "100%",
        height: "100%",
        borderRadius: "15px",
        overflow: "hidden",
    },
    metricBarZone: {
        flex: "1",
        opacity: "0.3",
    },
    metricIndicator: {
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: "2",
    },
    metricIndicatorCircle: {
        width: "36px",
        height: "36px",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
        transform: "translateX(-50%)",
    },
    metricScale: {
        display: "flex",
        justifyContent: "space-between",
        width: "100%",
        fontSize: "12px",
        color: "#8d6e63",
        marginTop: "8px",
    },
    metricUnit: {
        position: "absolute",
        right: "0",
        bottom: "0",
        fontSize: "12px",
        color: "#a18072",
        fontWeight: "500",
    },
    metricDescription: {
        marginTop: "5px",
    },
    descriptionText: {
        margin: "0",
        fontSize: "14px",
        color: "#6d4c41",
        fontStyle: "italic",
        lineHeight: "1.4",
    },

};

// Insert the CSS keyframes
document.head.appendChild(document.createElement('style')).textContent = keyframes;

export default OrgDashboard;